/**
 * EVAS v3 — CANONICAL CROSS-MODULE CONTRACTS.
 * Single source of truth (PLAN_00 §3). Never redefine these elsewhere.
 * Every payload is versioned; bump `v` on breaking change and keep readers tolerant.
 */
import { z } from "zod";
import { BROWSER_TOKEN_HASH_PATTERN, isBrowserToken } from "./browser-token.js";
import { GeoIpResult } from "./geoip.js";
// ---------- i18n ----------
// Supported UI locales (BCP-47). Shared so the console (next-intl) and the widget (data-driven
// FormConfig.labels) agree on locale codes. `en-US` ships first; `es-US` next. Form labels are
// admin-authored per Form and are NOT constrained to this list.
export const SUPPORTED_LOCALES = ["en-US", "es-US"];
export const Locale = z.enum(SUPPORTED_LOCALES);
export const DEFAULT_LOCALE = "en-US";
// ---------- enums ----------
export const Role = z.enum(["SUPER_ADMIN", "COMPANY_ADMIN", "COMPANY_MANAGER"]);
export const ContactMode = z.enum(["EMAIL_JOIN_URL", "PSTN_OUTBOUND", "PSTN_INBOUND_PIN"]);
export const SessionState = z.enum([
    "CREATED",
    "ACTIVE",
    "MANAGER_REQUESTED",
    "MANAGER_JOINED",
    "ENDED",
    "FAILED",
]);
export const ReportStatus = z.enum(["PENDING", "GENERATING", "COMPLETE", "FAILED"]);
/**
 * DID THE SESSION BECOME A LEAD? `pollLeadvalStatus`'s answer, in the poll's own vocabulary (EVAS-172).
 *
 * Lives HERE rather than in `leadval.ts` for the reason the note below this block gives: `SessionEventPayload`
 * embeds it, and `leadval.ts` imports from this module, never the reverse. It moved out of
 * `packages/leadval-client/src/sessions.ts` when the DB and the console both began to branch on it — at that
 * point it is a cross-module contract, and three copies of an enum is how two of them drift (AGENTS.md §6).
 *
 * ⚠ **`pending` IS NOT SUCCESS, AND TODAY IT IS THE NORMAL OUTCOME.** It means *"the session was submitted
 * and no verdict has been recorded yet"* — nothing more. LeadVal decides asynchronously and slowly: measured
 * 2026-09-16 on one real session, the conformance grade landed at notify + 111 s and the terminal answer
 * (`FAILED`, no lead) at + 18 min. Since EVAS-204 the agent does not wait at all — that wait ran inside
 * LiveKit session shutdown, so it could never reach those numbers — which means essentially every connected
 * session records `pending` until the mechanism EVAS-203 settles on fills it in.
 *
 * Two consequences worth stating where the values are defined. Anything that renders `pending` as a tick
 * reproduces, in the UI, the bug EVAS-170 fixed in the code — on that same measured session the *grade* was
 * `ACCEPTED_WITH_NOTES` while processing later failed outright. And no user-facing string may explain
 * `pending` by describing OUR implementation ("we stopped watching"): it is a fact about the session's
 * progress, not about how long we chose to look.
 */
export const LeadvalPollOutcome = z.enum([
    "conformant",
    "accepted-with-notes",
    "rejected",
    "failed",
    "not-evaluated",
    "pending",
    "unavailable",
]);
/**
 * The same seven answers as the DATABASE spells them. MIRRORS `enum LeadvalOutcome` in
 * `dataconnect/schema/schema.gql` — never diverge.
 *
 * Why two spellings exist at all, since duplication is what §6 warns against: a GraphQL enum value cannot
 * contain a hyphen, and the Data Connect compiler says so outright — feeding it `accepted-with-notes` answers
 * `Expected Name, found <Invalid>` (measured 2026-09-15). So the poll's kebab vocabulary is unrepresentable
 * as a column type, and the choice is between renaming the poll's values or mapping them. Mapping, because
 * {@link LEADVAL_OUTCOME_DB} makes the correspondence a compile-time obligation rather than a convention.
 */
export const LeadvalOutcome = z.enum([
    "CONFORMANT",
    "ACCEPTED_WITH_NOTES",
    "REJECTED",
    "FAILED",
    "NOT_EVALUATED",
    "PENDING",
    "UNAVAILABLE",
]);
/**
 * Poll answer → column value. The ONE place the two spellings are related.
 *
 * Typed as a total `Record` on purpose: adding a value to {@link LeadvalPollOutcome} without mapping it is a
 * COMPILE error, not a runtime surprise, and a stray key is an error too. The unit test adds what the type
 * system cannot — that the mapping is injective, so two poll answers can never collapse into one column
 * value and quietly merge two different fates.
 */
export const LEADVAL_OUTCOME_DB = {
    conformant: "CONFORMANT",
    "accepted-with-notes": "ACCEPTED_WITH_NOTES",
    rejected: "REJECTED",
    failed: "FAILED",
    "not-evaluated": "NOT_EVALUATED",
    pending: "PENDING",
    unavailable: "UNAVAILABLE",
};
// ---------- visitor context (EVAS-136 — LeadVal epic Phase 1; docs/leadval/02 §2C, §6 Q3/Q4) ----------
// These live HERE (not in leadval.ts) because core contracts below (JoinRequest,
// AgentDispatchMetadata, EvasAgentParams) embed them — leadval.ts imports from this module, never
// the reverse.
/** Processor modes for LeadVal session notify, as a LABELLED REGISTRY rather than a hand-listed
 * union — LeadVal adds verticals, and the previous `0|1|2` union went stale within a week of being
 * written (mode 3 measured live on 2026-08-27; EVAS-151).
 *
 * Source of truth is LeadVal's own `PROCESSOR_LABELS`; they generate the `PROCESSOR_UNSUPPORTED`
 * 422 text from it, so that prose drifts by design (docs/leadval/10 Round-11 §3 asks them for a
 * machine-readable list instead). Modes 0/1 are pipeline DEPTHS, not verticals: 0 = automation only,
 * 1 = identity only (no conformance, no scoring). Modes ≥ 2 are full verticals — the only ones that
 * run conformance + scoring + pricing.
 *
 * Keeping this narrower than LeadVal's registry can only block OUR configuration, never break a live
 * session: their ingestion refuses an unregistered mode outright (and note their own recorded hazard,
 * that an unregistered mode already *stored* on a lead resolves to 2 — i.e. reads as real estate). */
export const LEADVAL_PROCESSORS = {
    0: "Automation only",
    1: "Identity only",
    2: "Real estate",
    3: "Life insurance (US)",
};
export const LeadvalProcessor = z.union(Object.keys(LEADVAL_PROCESSORS).map((k) => z.literal(Number(k))));
export const LEADVAL_DEFAULT_PROCESSOR = 2;
/** Display label for the per-AiAgent processor picker; unknown modes render as the bare number so a
 * newly-registered LeadVal vertical is visible rather than blank. */
export function leadvalProcessorLabel(mode) {
    return LEADVAL_PROCESSORS[mode] ?? `Mode ${mode}`;
}
/** Narrow an arbitrary number onto the processor registry, or null.
 *
 * Needed because the zod schema above is BUILT from the registry keys at runtime, so its inferred type
 * widens to `number` while {@link LeadvalProcessor} is the narrow `0 | 1 | 2 | 3` union. Anything read
 * back out of `EvasAgentParams.leadval.processor` therefore arrives as `number` even though zod has
 * already validated it, and a consumer that must hand LeadVal a typed mode needs one honest narrowing
 * point rather than a cast per call site. */
export function asLeadvalProcessor(mode) {
    if (mode === undefined || mode === null)
        return null;
    return mode in LEADVAL_PROCESSORS ? mode : null;
}
/**
 * How much of the device the widget's collector reads (EVAS-413; design:
 * docs/research/visitor-identity-and-personalization-research.md §5.2). One collector, four modes, in
 * increasing order of what they read — the ORDER of this enum is the order {@link
 * effectiveFingerprintMode} compares on, so never reorder it:
 *   - `off` — nothing;
 *   - `lite` — the browser's own description: device type, OS, user agent, language, time zone. No hashes,
 *     no `fingerprintId`;
 *   - `identity` — `lite` plus the canvas hash (with its integrity verdict), the WebGL hash and the battery
 *     level: exactly the traits LeadVal scores. No `fingerprintId`;
 *   - `full` — everything the collector read before EVAS-413, byte for byte (LeadVal's ingestion asks for it).
 * The trait list of each mode is {@link FINGERPRINT_MODE_TRAITS} in `./fingerprint-modes.js`.
 */
export const FingerprintMode = z.enum(["off", "lite", "identity", "full"]);
/**
 * The scorer that recognises a returning visitor of a company WITHOUT a LeadVal connection. One pipeline, two
 * scorers (EVAS-460, research doc §6 decision 10):
 *   - `evas-rules` — the same contact and the same name; or, with {@link IdentityConfig} `useDeviceData`, the
 *     same device and the same name when the visitor typed more than a first name. A device alone never
 *     recognises anyone. The default; formerly `contact-name`.
 *   - `leadval-scoring` — LeadVal's scorer, copied unchanged (EVAS-415, `packages/agent-core/src/
 *     leadval-scoring.ts`), with our stricter rules: only a MATCH files the session under a participant and
 *     reads their profile.
 * A connected company keeps LeadVal's own lookup under `IdentityConfig.mode` whatever this says.
 */
export const IdentityMatching = z.enum(["evas-rules", "leadval-scoring"]);
/** {@link IdentityMatching} as stored: `contact-name`, EVAS rules' name before EVAS-460, reads as `evas-rules`.
 * The console has offered it as an explicit choice since EVAS-415, so an AI Agent may hold it, and one field
 * that fails to parse makes the agent drop every param it was dispatched (`mergeParams` in agent-core). */
export const StoredIdentityMatching = z.preprocess((v) => (v === "contact-name" ? "evas-rules" : v), IdentityMatching);
/** `IdentityConfig.matching` when unset. */
export const DEFAULT_IDENTITY_MATCHING = "evas-rules";
/** How closely two names must agree to be one person (EVAS-460, decision 10), for EVAS rules and for rule 5 of
 * LeadVal scoring: `strict`, the same words; `flexible`, a word may be left out or shortened to its initial,
 * and nicknames and close spellings count. In both, the first given name must be the same (in flexible, or its
 * nickname or close spelling). */
export const NameMatching = z.enum(["strict", "flexible"]);
/** `IdentityConfig.names` when unset: strict, privacy first. */
export const DEFAULT_NAME_MATCHING = "strict";
/** `IdentityConfig.useDeviceData` when unset. */
export const DEFAULT_USE_DEVICE_DATA = false;
/** `IdentityConfig.nameCheck` (rule 5) when unset. */
export const DEFAULT_NAME_CHECK = true;
/** How `identifyParticipant` uses the LeadVal identity lookup (report 03 §5-bis decision 3):
 *   - `augment` — LeadVal's verdict joins the existing ladder. An `EXACT_MATCH` links deterministically;
 *     anything weaker becomes evidence for the judge LLM, together with candidates only LeadVal could
 *     find (it sees this person across channels and devices our email/phone query cannot).
 *   - `replace` — LeadVal decides and the judge LLM is never called. Deterministic and cheaper; an exact
 *     LOCAL email match still wins, because that is not a probabilistic claim.
 *   - `off` — the pre-Phase-5 behaviour.
 *
 * ⚠ `off` is ALSO the effective mode for any company with no LeadVal connection, regardless of this
 * setting — there is no key to call with. That is enforced where the client is built (agent-core), not
 * here: this schema describes an intent, and whether it is reachable is a property of the company.
 *
 * Named rather than inline (EVAS-140) so agent-core can import the default instead of repeating the
 * string, and because AGENTS.md §2 already names `IdentityConfig` as one of the contracts that live in
 * this file.
 *
 * EVAS-413 adds two OPTIONAL fields, so every stored row parses unchanged:
 *   - `matching` — {@link IdentityMatching}; unset = {@link DEFAULT_IDENTITY_MATCHING}.
 *   - `fingerprint` — the {@link FingerprintMode} an operator asked for. Never read directly: the mode the
 *     widget collects is `effectiveFingerprintMode`, which raises it to what the agent needs. Unset = the
 *     agent's default, `fingerprintModeDefault` (its floor, but `lite` where the floor is `off`). */
export const IdentityConfig = z.object({
    mode: z.enum(["augment", "replace", "off"]).default("augment"),
    matching: StoredIdentityMatching.optional(),
    fingerprint: FingerprintMode.optional(),
    // EVAS-460 (decision 10), all optional so every stored row parses unchanged; unset takes the default.
    /** Find returning visitors by their consented device too. Raises the device data to Identity. */
    useDeviceData: z.boolean().optional(),
    /** {@link NameMatching}. */
    names: NameMatching.optional(),
    /** Rule 5, LeadVal scoring only: never MATCH a participant the Names setting calls another person. */
    nameCheck: z.boolean().optional(),
});
/** {@link RecognizerSettings} from a loose `identity` slice. */
export function recognizerSettings(identity) {
    return {
        scorer: identity?.matching ?? DEFAULT_IDENTITY_MATCHING,
        useDeviceData: identity?.useDeviceData ?? DEFAULT_USE_DEVICE_DATA,
        names: identity?.names ?? DEFAULT_NAME_MATCHING,
        nameCheck: identity?.nameCheck ?? DEFAULT_NAME_CHECK,
    };
}
/** The mode when `EvasAgentParams.identity` is ABSENT — the common case, since no console field writes
 * it yet (the descriptor-driven params editor is EVAS-23; the raw params JSON textarea can set it today).
 *
 * `augment` and not `off`, which is the same choice `LEADVAL_DEFAULT_PROCESSOR` makes: a connected
 * company should get the integration it was connected for without an operator having to opt every AI
 * Agent in one at a time. Safe as a default because `augment` only ADDS a signal — it links
 * deterministically solely on an `EXACT_MATCH`, hands everything weaker to the judge that already
 * decided these cases, and cannot greet anyone by name unless `matches[0].nameUse` says so. */
export const DEFAULT_IDENTITY_MODE = "augment";
/**
 * How a Participant↔Session link was decided — PROVENANCE, and the first thing an audit of a WRONG link
 * needs. Mirrors the `LinkMethod` enum in `dataconnect/schema` — never diverge.
 *
 *   · `MANUAL` — a human, in the console.
 *   · `DETERMINISTIC` — an exact match on a contact field we already hold (today email, company-scoped),
 *     or (EVAS-415) a MATCH of LeadVal's scoring run by us for a `leadval-scoring` agent; no model decides
 *     either, and the session's `CUSTOM` `IDENTITY_SCORING` event says which.
 *     It outranks everything else, so this is the value that appears beside an
 *     `IDENTITY_LOOKUP: EXACT_MATCH` when LeadVal agreed but did not decide.
 *     Also (EVAS-449) a new participant when no candidate is left for a judge: none was found, or each one
 *     was kept apart as another person (the session's `PARTICIPANT_POSSIBLE_MATCH` events name them).
 *   · `LEADVAL` — their calibrated matcher over the workspace's own leads asserted it.
 *   · `LLM` — our judge model decided, over the candidate rows it was given.
 *   · `FALLBACK` — the no-judge heuristic (single candidate, or create), which runs exactly when a judge had
 *     candidates to weigh and no model was available, or in `replace` mode, which never asks one.
 *
 * ⚠ Rows written before 2026-09-12 all say `LLM` whatever decided them: `DETERMINISTIC` and `FALLBACK`
 * did not exist, and the deciding mechanism was never recorded, so there is nothing to backfill from.
 *
 * Named here (EVAS-140) rather than inlined in {@link SessionEventPayload} and re-declared in agent-core,
 * which is what it was until the AI review caught it — three copies of one enum, two of which I had just
 * widened. AGENTS.md §6: never redefine a contract locally.
 */
export const LinkMethod = z.enum(["LLM", "MANUAL", "LEADVAL", "DETERMINISTIC", "FALLBACK"]);
/** Compliance-critical consent value (LeadVal `metadata.consent.contactConsent`). Derived by the
 * join fn from the form: consent field checked → GRANTED, explicitly declined → DECLINED, field
 * disabled or absent → NOT_REQUESTED. */
export const ContactConsent = z.enum(["GRANTED", "DECLINED", "NOT_REQUESTED"]);
/** The visitor's answer to the device-recognition checkbox (EVAS-413, research doc §6 decision 7), in the
 * same three values and by the same derivation rule as {@link ContactConsent}: ticked → GRANTED, shown and
 * left unticked → DECLINED, not shown or not answered → NOT_REQUESTED. The same enum, named for what it
 * answers — not a second copy of it. */
export const DeviceConsent = ContactConsent;
/** Host-page attribution captured by the widget and sent with the join request (fills LeadVal's
 * MANDATORY `metadata.embedding`). UNTRUSTED client-supplied data — size-capped here, treated as
 * attribution-grade only, never authorization. The join fn merges server-known values
 * (embeddingId, room) when assembling the ingestion metadata. */
export const PageContext = z.object({
    pageUrl: z.string().max(2048).optional(),
    referrer: z.string().max(2048).optional(),
    utm: z.record(z.string().max(64), z.string().max(256)).optional(),
    clickIds: z.record(z.string().max(64), z.string().max(256)).optional(), // gclid/fbclid/…
});
/** Output of `collectFingerprint()` (`packages/fingerprint`, Phase 3 — the vendored
 * fingerprinting-v7.js refactored: DOM hooks and the client-side IP/geo call REMOVED;
 * `transientMetadata.ipAddress` + `georesolver` are filled SERVER-side from the join fn's IP + the
 * geoip provider, which is also LeadVal's own documented guidance since 2026-08-19). Forwarded
 * UNMODIFIED into LeadVal's `metadata.fingerprint` (stringified exactly once); hashes stay hashes;
 * absence over fabrication. Loose on purpose: the collector's trait set evolves without a contract
 * change.
 *
 * `fingerprintId` is OPTIONAL since EVAS-413: only the `full` mode mints one. `lite` and `identity` hash a
 * different trait set, so an id from them could never equal a `full` id for the same device — they carry
 * none rather than an id that looks comparable and is not (packages/fingerprint/DIVERGENCES.md D9). */
export const FingerprintResult = z
    .object({
    fingerprintId: z.string().min(1).optional(),
    staticTraits: z.record(z.string(), z.unknown()),
    transientMetadata: z.record(z.string(), z.unknown()).optional(),
    georesolver: z.record(z.string(), z.unknown()).optional(),
    /** The shipped emitter stamps `timestamp`; LeadVal accepts `capturedAt|timestamp`. */
    timestamp: z.string().optional(),
    capturedAt: z.string().optional(),
})
    .passthrough();
// ---------- Form ----------
// EVAS-458 part 2 (research doc §6 decision 10): `name` is the form's Name field, one string as typed, and
// `firstName` / `lastName` the First name + Last name option's two fields. A Form shows one or the other, or
// neither (`nameFieldsProblem` in ./form-name-options.js); the order here is the order the widget renders.
export const FormFieldName = z.enum([
    "name",
    "firstName",
    "lastName",
    "email",
    "phone",
    "language",
    "country",
    "zip",
    "message",
]);
export const FormFieldConfig = z.object({ enabled: z.boolean(), required: z.boolean() });
/** Every key an admin may author in `FormConfig.labels[locale]`: the input labels plus the chrome
 * strings that are part of the form screen (`title` / `submit` / `thankYou`) and the consent sentence.
 * Named so the built-in copy that backs these keys can be typed against them — see
 * {@link DEFAULT_FORM_LABELS} in `./form-labels.js`. */
export const FormLabelKey = FormFieldName.or(
// `deviceConsent` / `deviceConsentHelp` (EVAS-413): the device-recognition checkbox's sentence and the
// short explanation under it.
z.enum(["submit", "title", "thankYou", "contactConsent", "deviceConsent", "deviceConsentHelp"]));
// A complete `fields` map, every FormFieldName present (a missing one off). `FormConfig.fields` itself is
// partial since EVAS-458 part 2: a field a Form does not list is off, so adding a field (the `name` field)
// leaves every stored row valid, as EVAS-136 made the labels partial for the same reason.
export function completeFormFields(partial = {}) {
    const out = {};
    for (const f of FormFieldName.options)
        out[f] = partial[f] ?? { enabled: false, required: false };
    return out;
}
export const FormConfig = z.object({
    v: z.literal(1),
    // PARTIAL record (EVAS-458 part 2): a field not listed is off. Readers index it as `fields[f]?.enabled`.
    fields: z.partialRecord(FormFieldName, FormFieldConfig),
    // lang -> key -> text. PARTIAL record (EVAS-136): zod-4 enum-keyed records are exhaustive, so
    // adding the `contactConsent` key to a plain z.record would have invalidated every stored Form row
    // lacking the new label. Labels are inherently sparse per language; consumers already fall back on
    // defaults for missing keys.
    labels: z.record(z.string(), z.partialRecord(FormLabelKey, z.string())),
    colors: z.record(z.string(), z.string()), // CSS custom-property tokens, e.g. { "primary": "#0a84ff" }
    language: z.string().default("en"),
    // EVAS-136 (LeadVal epic, decision Q4 in docs/leadval/02 §6): the contact-consent checkbox.
    // NOT a FormFieldName — it is a boolean checkbox, not a text input, and its default differs
    // (enabled+required by default; both flags clearable, since LOCAL companies may not use it).
    // Existing rows parse unchanged: absent → the default applies.
    contactConsent: FormFieldConfig.default({ enabled: true, required: true }),
    // EVAS-413 (research doc §6 decision 7): the device-recognition consent checkbox. OPTIONAL with no
    // schema default, unlike `contactConsent`, because its default depends on the AI Agent behind the
    // embedding, not on the Form: `resolveDeviceConsent` (./fingerprint-modes.js) shows it — enabled and
    // required unless the Form says otherwise — only when the effective fingerprint mode is `identity` or
    // `full`. In an `EmbedConfig` the join function writes the RESOLVED value, so the widget never has to
    // know the rule. Existing rows parse unchanged: absent → that default.
    deviceConsent: FormFieldConfig.optional(),
});
/**
 * A blank optional field is ABSENT, not an empty string (EVAS-188).
 *
 * ⚠ THIS IS LOAD-BEARING, AND IT COST A VISITOR-FACING OUTAGE. `email` carries `.email()`, so `""` is a
 * VALIDATION FAILURE rather than a harmless empty value — and a widget sends `""` for any field the visitor
 * typed into and then cleared. Measured on dev 2026-09-13: `formData: { lastName: "Jhonson", email: "" }`
 * failed `JoinRequest.safeParse`, which the join function refuses at its earliest gate with no CORS headers,
 * so the browser could not read the reason and the visitor saw "NetworkError when attempting to fetch
 * resource". A form whose email box is optional silently lost every visitor who touched and cleared it.
 *
 * Normalising HERE rather than only in the widget is deliberate: this schema is the seam both sides meet at,
 * and a widget bundle cached on a host page keeps sending `""` for months after the widget is fixed — the
 * same reasoning `handlers.ts` already applies to consent payloads from pre-EVAS-138 bundles.
 *
 * Applied to every optional string field, not just `email`: the others merely tolerated `""` and then stored
 * it, which is how a Participant ends up with `name: ""` instead of no name.
 */
const blankToUndefined = (schema) => z.preprocess((v) => (typeof v === "string" && v.trim() === "" ? undefined : v), schema);
export const FormData = z.object({
    // EVAS-458 part 2: the Name field, never split; the two below are the First name + Last name option.
    name: blankToUndefined(z.string().optional()),
    firstName: blankToUndefined(z.string().optional()),
    lastName: blankToUndefined(z.string().optional()),
    email: blankToUndefined(z.string().email().optional()),
    phone: blankToUndefined(z.string().optional()),
    language: blankToUndefined(z.string().optional()),
    country: blankToUndefined(z.string().optional()),
    zip: blankToUndefined(z.string().optional()),
    message: blankToUndefined(z.string().max(2000).optional()),
    // EVAS-136: consent checkbox value. true/false = the visitor answered; absent = not shown.
    // The join fn maps it to {@link ContactConsent} (GRANTED / DECLINED / NOT_REQUESTED).
    contactConsent: z.boolean().optional(),
    // EVAS-413: the device-recognition checkbox, same encoding. The join fn maps it to {@link DeviceConsent}
    // and keeps no device traits beyond `lite` unless it is `true` while the checkbox is shown.
    deviceConsent: z.boolean().optional(),
});
// ---------- widget <-> join function ----------
export const EmbedConfig = z.object({
    v: z.literal(1),
    embeddingId: z.string(), // public embedKey, NOT internal UUID
    enabled: z.boolean(),
    theme: z.object({ mode: z.enum(["light", "dark", "system"]).default("system") }).passthrough(),
    form: FormConfig,
    language: z.string(),
    agentClientModule: z.string().url().optional(), // lazy per-agent client plugin chunk
    assistantName: z.string().optional(), // AI Agent display name, shown in widget tiles/captions
    // EVAS-413: what the widget's collector may read — the EFFECTIVE mode (`effectiveFingerprintMode`), never
    // below what the agent needs. ABSENT = `full`, which is what every widget collected before EVAS-413, so a
    // config from an older join function keeps today's behaviour.
    fingerprintMode: FingerprintMode.optional(),
});
export const JoinRequest = z.object({
    embeddingId: z.string(),
    formData: FormData,
    // EVAS-136 (wired by the widget in Phase 3): host-page attribution + the device fingerprint.
    // Both optional and untrusted; the join fn validates, size-caps and forwards them.
    pageContext: PageContext.optional(),
    fingerprint: FingerprintResult.optional(),
    // EVAS-459: the returning-browser token (`./browser-token.js`), sent only when the device-recognition box
    // was ticked. Untrusted. A value that is not a token the widget could have made is DROPPED, never refused:
    // a refused join is a visitor who sees only a network error (EVAS-188). The join keeps its hash only.
    browserToken: z.preprocess((v) => (isBrowserToken(v) ? v : undefined), z.string().optional()),
});
export const JoinResponse = z.object({
    token: z.string(),
    wsUrl: z.string().url(),
    roomName: z.string(),
    sessionId: z.string(),
});
// ---------- join function -> agent (LiveKit dispatch metadata, JSON-stringified) ----------
// v2 (EVAS-136): adds the visitor-context block — pageContext, fingerprint, server-side IP/geo
// lookup result, and the derived consent value. Purely ADDITIVE (all new fields optional), so both
// versions parse with this schema and readers stay tolerant: v1 payloads from an older join fn are
// valid v2-less inputs, and the agent treats absent context as "not collected". The join fn emits
// v: 2 once it populates any of the new fields (Phase 3).
export const AgentDispatchMetadata = z.object({
    v: z.union([z.literal(1), z.literal(2)]),
    sessionId: z.string(),
    embeddingId: z.string(),
    companyId: z.string(),
    formData: FormData,
    // Optional: an Embedding may have no manager (pure-AI, no human handoff). When absent, the agent
    // offers no manager-handoff path.
    manager: z.object({ userId: z.string(), contactMode: ContactMode }).optional(),
    agentParams: z.record(z.string(), z.unknown()).default({}),
    // ---- v2 visitor context (EVAS-136) ----
    pageContext: PageContext.optional(),
    fingerprint: FingerprintResult.optional(),
    /** Client IP as seen by the join fn (GFE X-Forwarded-For) — server-observed, never client-supplied. */
    clientIp: z.string().optional(),
    /** IP-intelligence lookup result (packages/geoip, resolved in the join fn; fail-open → absent). */
    geo: GeoIpResult.optional(),
    /** Consent derived by the join fn from the form config + submitted value. */
    contactConsent: ContactConsent.optional(),
    /** EVAS-413: the device-recognition answer, derived the same way. */
    deviceConsent: DeviceConsent.optional(),
    /** EVAS-413: the trait set `fingerprint` was restricted to — the effective mode, or `lite` when the
     * device-recognition checkbox was shown and not ticked. Absent from a pre-EVAS-413 join fn (= `full`). */
    fingerprintMode: FingerprintMode.optional(),
    /** EVAS-459: the returning-browser token's hash (SHA-256 hex over the company id, ":" and the token), set by
     * the join only when `deviceConsent` is GRANTED. Device evidence: it finds candidates, never recognises
     * anyone alone. A value of any other shape is dropped, so a stray one cannot fail the dispatch. */
    browserTokenHash: z.preprocess((v) => (typeof v === "string" && BROWSER_TOKEN_HASH_PATTERN.test(v) ? v : undefined), z.string().optional()),
});
// ---------- AI Agent params (avatar + brain providers) ----------
// Typed shape of `AiAgent.params` (DB JSONB), authored super-admin-side and forwarded verbatim into
// `AgentDispatchMetadata.agentParams` (which stays a loose record on the wire). The agent runtime
// (`@evas/agent-core`) validates the incoming `agentParams` against `EvasAgentParams` and resolves the
// provider via its avatar/brain registries. Providers are intentionally open-ended: v3 wires LiveAvatar
// + OpenAI Realtime; Tavus/Simli and Gemini Live / STT-LLM-TTS are typed but unimplemented (the agent
// throws a clear "provider not yet supported" until wired). `options` carries provider-specific knobs
// so adding a provider needs no contract change.
export const AvatarProvider = z.enum(["liveavatar", "tavus", "simli"]);
export const AvatarVideoQuality = z.enum(["very_high", "high", "medium", "low"]);
export const AvatarConfig = z.object({
    provider: AvatarProvider,
    avatarId: z.string().optional(), // falls back to provider env (e.g. LIVEAVATAR_AVATAR_ID) when absent
    videoQuality: AvatarVideoQuality.optional(),
    options: z.record(z.string(), z.unknown()).default({}), // provider-specific extras
});
export const BrainProvider = z.enum(["openai-realtime", "gemini-live", "pipeline"]);
// ---------- OpenAI Realtime session knobs (EVAS-122) ----------
// These are the `openai.realtime.RealtimeModel` options worth authoring per AI Agent, typed here so
// the console can offer them as fields instead of hand-typed JSON. Every one of them is FORWARDED by
// `@evas/agent-core`'s `realtimeModelOptions`; `BrainConfig.options` remains the escape hatch for
// anything not modeled (it is forwarded too, and wins — see that function).
/** Reasoning effort for the reasoning-capable Realtime models (`gpt-realtime-2*`). Absent = the
 * catalog's `REALTIME_DEFAULT_REASONING` for such a model, nothing for the others. Deliberately NOT
 * the portable `LlmReasoning` used by `@evas/llm`: that one carries "default"/"none" members which
 * the Realtime API has no equivalent for (absence means default here), and the two axes version
 * independently. Levels + guidance per OpenAI's realtime prompting guide (checked 2026-08). */
export const RealtimeReasoningEffort = z.enum(["minimal", "low", "medium", "high", "xhigh"]);
/** Server-side turn detection — the barge-in / interruption feel, and the thing that decides when the
 * visitor's turn has ended. `semantic_vad` (model-judged, the plugin default) is right for open-ended
 * conversation; `server_vad` (energy + silence timers) is predictable but cuts thinkers off mid-pause.
 * `off` disables endpointing entirely: nothing then commits the input buffer on its own, so it is a
 * debugging setting, not a production one. Interacts with P5.2 post-handoff voice gating, which mutes
 * the agent's audio separately. */
export const RealtimeTurnDetection = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("semantic_vad"),
        // How eager the model is to treat a pause as end-of-turn (plugin default: medium).
        eagerness: z.enum(["auto", "low", "medium", "high"]).optional(),
    }),
    z.object({
        type: z.literal("server_vad"),
        threshold: z.number().min(0).max(1).optional(), // speech-detection sensitivity (default 0.5)
        prefixPaddingMs: z.number().int().nonnegative().optional(), // audio kept before speech (default 300)
        silenceDurationMs: z.number().int().nonnegative().optional(), // silence = end of turn (default 200)
    }),
    z.object({ type: z.literal("off") }),
]);
/** Input noise reduction. Website visitors on laptop/headset mics are the `near_field` case;
 * `far_field` suits room/speakerphone audio. `off` = the API default (no reduction). */
export const RealtimeNoiseReduction = z.enum(["off", "near_field", "far_field"]);
/** The BRAIN's own input transcription — what shows up as the visitor's transcript on the realtime
 * session. NOT the per-track STT sidecar (`packages/agent-core/src/stt.ts`, `gpt-realtime-whisper`),
 * which is a separate session and the only thing that hears the MANAGER. `off` disables the brain's
 * transcription (the sidecar still transcribes the visitor). Anything outside this set can still be
 * set through `BrainConfig.options.inputAudioTranscription`. */
export const RealtimeInputTranscription = z.enum([
    "off",
    "gpt-4o-mini-transcribe", // plugin default
    "gpt-4o-transcribe",
    "gpt-realtime-whisper", // matches the sidecar
]);
export const BrainConfig = z.object({
    provider: BrainProvider,
    // Realtime model id. Absent = `REALTIME_DEFAULT_MODEL` — resolved by `@evas/agent-core`, NOT left
    // to the plugin, whose own default is still the superseded `gpt-realtime` (EVAS-122).
    model: z.string().optional(),
    voice: z.string().optional(), // REALTIME_VOICES member; absent = REALTIME_DEFAULT_VOICE
    instructions: z.string().optional(), // appended to the assembled EVAS persona
    reasoning: RealtimeReasoningEffort.optional(),
    turnDetection: RealtimeTurnDetection.optional(),
    noiseReduction: RealtimeNoiseReduction.optional(),
    // Spoken-output rate. OpenAI's documented range; live-verify against LiveAvatar lipsync before
    // moving it off 1.0 — the avatar animates from the audio it receives.
    speed: z.number().min(0.25).max(1.5).optional(),
    inputTranscription: RealtimeInputTranscription.optional(),
    maxOutputTokens: z.union([z.literal("inf"), z.number().int().positive()]).optional(),
    // Escape hatch for un-modeled plugin options, forwarded verbatim (and last, so it overrides the
    // typed fields above). NOTE: `temperature` used to live on this contract as a typed field and was
    // removed in EVAS-122 — the Realtime GA API dropped it and the plugin marks it deprecated/unused,
    // so it was a silent no-op. Rows that still carry it parse fine (zod strips unknown keys).
    options: z.record(z.string(), z.unknown()).default({}),
});
const REALTIME_REASONING_LEVELS = [
    "minimal",
    "low",
    "medium",
    "high",
    "xhigh",
];
export const REALTIME_MODELS = [
    {
        id: "gpt-realtime-2.1",
        label: "GPT Realtime 2.1",
        pricing: { audioInput: 32, audioOutput: 64, textInput: 4, textOutput: 24, imageInput: 5 },
        contextWindow: 128_000,
        maxOutput: 32_000,
        reasoningLevels: REALTIME_REASONING_LEVELS,
    },
    {
        id: "gpt-realtime-2.1-mini",
        label: "GPT Realtime 2.1 mini",
        pricing: { audioInput: 10, audioOutput: 20, textInput: 0.6, textOutput: 2.4, imageInput: 0.8 },
        contextWindow: 128_000,
        maxOutput: 32_000,
        reasoningLevels: REALTIME_REASONING_LEVELS,
    },
    {
        id: "gpt-realtime-2",
        label: "GPT Realtime 2",
        pricing: { audioInput: 32, audioOutput: 64, textInput: 4, textOutput: 24, imageInput: 5 },
        contextWindow: 128_000,
        maxOutput: 32_000,
        reasoningLevels: REALTIME_REASONING_LEVELS,
        supersededBy: "gpt-realtime-2.1", // identical price/window/cutoff; 2.1 only improves behavior
    },
    {
        id: "gpt-realtime-1.5",
        label: "GPT Realtime 1.5 (non-reasoning)",
        pricing: { audioInput: 32, audioOutput: 64, textInput: 4, textOutput: 16, imageInput: 5 },
        contextWindow: 32_000,
        maxOutput: 4_096,
        reasoningLevels: null,
    },
    {
        id: "gpt-realtime-mini",
        label: "GPT Realtime mini (non-reasoning)",
        pricing: { audioInput: 10, audioOutput: 20, textInput: 0.6, textOutput: 2.4, imageInput: 0.8 },
        contextWindow: 32_000,
        maxOutput: 4_096,
        reasoningLevels: null,
        // OpenAI deprecation announced 2026-07-20 (docs checked 2026-08-15).
        deprecated: { shutdownOn: "2027-01-20", replacement: "gpt-realtime-2.1-mini" },
    },
    {
        id: "gpt-realtime",
        label: "GPT Realtime (first GA — plugin default)",
        pricing: { audioInput: 32, audioOutput: 64, textInput: 4, textOutput: 16, imageInput: 5 },
        contextWindow: 32_000,
        maxOutput: 4_096,
        reasoningLevels: null,
        // OpenAI deprecation announced 2026-07-20 (docs checked 2026-08-15). This is what every agent
        // silently ran before EVAS-122 — and it stops working entirely on the shutdown date.
        deprecated: { shutdownOn: "2027-01-20", replacement: "gpt-realtime-2.1" },
    },
];
/** The pin. Every agent resolves to this when `BrainConfig.model` is absent, so the plugin's own
 * default (`gpt-realtime`, the first GA model: 32k window, 2023-10 cutoff, no reasoning) can never
 * silently apply again. */
export const REALTIME_DEFAULT_MODEL = "gpt-realtime-2.1";
/** Applied to reasoning-capable models when `BrainConfig.reasoning` is absent. `low` per OpenAI's
 * realtime prompting guide ("Start with `low` for most production voice agents", and its 2.x
 * migration step 2: "Set reasoning effort to `low` instead of the default") — this brain drives a
 * live avatar, so latency is a feature. */
export const REALTIME_DEFAULT_REASONING = "low";
/** Realtime voices per OpenAI's realtime-conversations guide (checked 2026-08); `marin`/`cedar` are
 * the recommended pair. Cannot change once a session has emitted audio. */
export const REALTIME_VOICES = [
    "marin",
    "cedar",
    "alloy",
    "ash",
    "ballad",
    "coral",
    "echo",
    "sage",
    "shimmer",
    "verse",
];
export const REALTIME_DEFAULT_VOICE = "marin";
/**
 * INPUT NOISE REDUCTION IS ON BY DEFAULT (EVAS-211). The plugin's own default is
 * `inputAudioNoiseReduction: undefined` — i.e. none at all — and we never set it, so every session up to
 * now ran with the visitor's raw microphone feed going to the model's VAD.
 *
 * `near_field` because a website visitor is at a laptop or wearing a headset; `far_field` is for room
 * and speakerphone audio. It is a soft mitigation, not the cure for echo of fluent speech — see
 * {@link REALTIME_DEFAULT_TURN_DETECTION} for the lever that actually targets it — but it costs nothing
 * and there is no argument for shipping with it off.
 */
export const REALTIME_DEFAULT_NOISE_REDUCTION = "near_field";
/**
 * TURN DETECTION IS NOW SET DELIBERATELY, AND `server_vad` IS A CONSIDERED CHOICE OVER THE PLUGIN'S
 * `semantic_vad` DEFAULT (EVAS-211).
 *
 * The measured problem: the avatar's own voice returns through the visitor's speakers into their
 * microphone, and OpenAI's VAD reads it as the visitor speaking. Measured on dev 2026-09-18 in a single
 * session with the microphone open then muted: **12.2 phantom detections per minute, then exactly zero**.
 * Consequences went well past a bad conversation — a phantom caused `request_manager` to dispatch a real
 * manager, and phantoms cancelled the spoken manager introduction so the agent then told the visitor
 * nobody had joined.
 *
 * WHY NOT `semantic_vad` with `eagerness: "low"`, the obvious first guess: semantic VAD scores *whether a
 * thought is complete*. Echoed avatar speech consists of well-formed complete sentences, so it scores
 * HIGH — the classifier is working correctly on audio it should never have received. Lowering eagerness
 * only lengthens the end-of-turn timeout; it does not make a fluent-sounding fragment stop looking like a
 * finished utterance.
 *
 * WHY `server_vad` WITH A RAISED `threshold`: echo arrives ATTENUATED — through a speaker, across the
 * room, and after the browser's own AEC has had a go at it. A loudness gate is therefore the one signal
 * that separates it from the visitor talking directly into their microphone, and it is exactly what
 * OpenAI documents the knob for: "a higher threshold will require louder audio to activate the model, and
 * thus might perform better in noisy environments".
 *
 * `silenceDurationMs` is raised well above the API's 200 ms because that default is what chopped continuous
 * audio into the 0.6–1.4 s "utterances" seen in the logs; 600 ms is a more human end-of-turn and further
 * reduces the number of fragments a burst can produce.
 *
 * ⚠ THIS IS A TUNING STARTING POINT, NOT A PROVEN SETTING. It is per-AI-Agent overridable and the values
 * are meant to be measured against the 12.2/min baseline, not trusted. If it proves insufficient, the
 * targeted fix is agent-side voice isolation (`inputOptions.noiseCancellation`), which suppresses
 * competing SPEECH rather than gating on loudness — that carries a per-minute cost, which is why it is a
 * deliberate decision rather than a default.
 */
export const REALTIME_DEFAULT_TURN_DETECTION = {
    type: "server_vad",
    threshold: 0.75,
    prefixPaddingMs: 300,
    silenceDurationMs: 600,
};
export function realtimeModelEntry(id) {
    return REALTIME_MODELS.find((m) => m.id === id) ?? null;
}
/** Blended $/1M for banding: audio-weighted 1:1 in/out because EVAS sessions are speech-to-speech
 * and audio tokens dwarf the text ones (a chat message is a rounding error next to a minute of
 * talking). Text prices are shown in the console but not banded. */
export function realtimeBlendedPrice(e) {
    return (e.pricing.audioInput + e.pricing.audioOutput) / 2;
}
/** Price band badge, same visual language as {@link llmPriceBand} but on the audio blend: today
 * that is `$$$$` for the full models ($48) and `$$` for the minis ($15). */
export function realtimePriceBand(e) {
    const p = realtimeBlendedPrice(e);
    if (p < 5)
        return "$";
    if (p < 20)
        return "$$";
    if (p < 35)
        return "$$$";
    if (p < 60)
        return "$$$$";
    return "$$$$$";
}
// Post-handoff behavior (PLAN_05 §P5, extended P5.2). Independent toggles composing the manager-handoff
// reactions; the LiveKit agent ALWAYS stays in the room gathering transcripts (it never hard-leaves):
//   introduceManager — speak a short spoken introduction when the manager joins.
//   closeAvatar      — tear down the LiveAvatar video session on join (the agent stays connected;
//                      still answers in chat, and via voice when addressed by `agentAddressName`).
//   voiceGating      — P5.2: PROGRAMMATIC post-handoff voice gating. When true (default), once a manager
//                      joins the agent's raw audio is muted and it speaks ONLY when a per-track STT hears
//                      `agentAddressName` (deterministic, not instruction-only). When false, the legacy
//                      soft instruction-only gating applies. Manager-VOICE attribution (per-track STT) is
//                      captured either way.
//   summarizeForManager — EVAS-117: on join, push the manager an UNSOLICITED short briefing (who the
//                      visitor is, what they want, what has been tried, the next step) over the P5.4
//                      private channel, so they arrive already briefed instead of typing "summarize this"
//                      and waiting while the visitor waits too.
//                      DEFAULT TRUE, unlike `closeAvatar`. It is the seconds right after a handoff that
//                      the feature buys back, it cannot leak (private topic + `destinationIdentities`,
//                      EVAS-14's three layers), and it costs one flash-tier call per handoff — so the
//                      useful default is on, and an operator turns it OFF for a reason rather than having
//                      to find it to get the benefit. Uses `EvasAgentParams.privateChat` for the model:
//                      one private-reply LLM setting, not two.
export const PostHandoffConfig = z.object({
    introduceManager: z.boolean().default(true),
    closeAvatar: z.boolean().default(false),
    voiceGating: z.boolean().default(true),
    summarizeForManager: z.boolean().default(true),
});
// ---------- LLM abstraction config (EVAS-96) ----------
// One config shape for every one-shot structured-output LLM use (reports, in-call judges). `provider`
// is the PLATFORM/auth domain, not the model vendor: Claude-on-Vertex = { provider: "vertex", model:
// "claude-opus-5-5" }; GPT-on-Bedrock = { provider: "bedrock", model: "openai.gpt-5.6-terra" }. Transport
// routing (Gemini vs Anthropic vs MaaS on Vertex; Converse vs Mantle on Bedrock) is resolved by
// `@evas/llm` from the model catalog (`LLM_CATALOG`) — invisible to call sites and stored config.
// "anthropic" (EVAS-349) is the Anthropic Platform, the vendor's own API with ANTHROPIC_API_KEY: the
// same Claude models as the vertex/bedrock routes, with no cloud marketplace in between (EVAS-311).
export const LlmProvider = z.enum(["vertex", "openai", "bedrock", "anthropic"]);
// Portable reasoning/thinking level, mapped by the AI SDK to each vendor's native knob (OpenAI
// reasoningEffort, Gemini thinkingLevel, Anthropic adaptive thinking/effort). "default" (or absent) =
// provider default. Per-model valid levels live in the catalog (`reasoningLevels`), and `@evas/llm`
// clamps a stored level onto them before the call — the SDK's mapping is per-vendor, NOT per-model, so
// it happily sends a level the specific model rejects (EVAS-126: "minimal" → Vertex 400 on 3.7 Flash,
// which 3.6 Flash accepted). Stored rows outlive model upgrades, so the clamp is the contract. Sampling params (temperature/top_p) are deliberately NOT part of the contract —
// deprecated on Gemini 3.6+/Claude 4.7+ and rejected by OpenAI reasoning models.
export const LlmReasoning = z.enum([
    "default",
    "none",
    "minimal",
    "low",
    "medium",
    "high",
    "xhigh",
]);
export const LlmConfig = z.object({
    provider: LlmProvider.default("vertex"),
    model: z.string().optional(), // catalog id; per-use-case default when absent (LLM_DEFAULTS)
    reasoning: LlmReasoning.optional(),
    // Provider-specific escape hatch, forwarded as AI SDK providerOptions. NOTE: reasoning-related keys
    // here silently override `reasoning` above (AI SDK precedence rule) — @evas/llm warns when both set.
    options: z.record(z.string(), z.unknown()).default({}),
});
// End-of-session report LLM (P6, generalized by EVAS-96): LlmConfig + delivery toggle. Existing rows
// ({ provider, model }) parse unchanged.
export const ReportConfig = LlmConfig.extend({
    // P6.1: email the finished report (md body + PDF attachment) to the manager who joined the session.
    // Sent post-generation by the report-generator fn, only when a MANAGER_JOINED event fired.
    emailToManager: z.boolean().default(true),
});
/**
 * EVAS-461 (research doc §6 decision 10, "With no name"): how the avatar addresses a visitor who gave no name
 * (the No name form option, or an optional name left empty).
 *   - `greet` — greets without a name. The default, and the behaviour before this setting.
 *   - `ask` — asks how to address the visitor. The spoken answer addresses them in that conversation only:
 *     it is never stored as an identity, never written to the participant, and never used for matching.
 */
export const NoNameGreeting = z.enum(["greet", "ask"]);
/** `EvasAgentParams.noNameGreeting` when unset. */
export const DEFAULT_NO_NAME_GREETING = "greet";
export const EvasAgentParams = z.object({
    v: z.literal(1),
    persona: z.string().optional(), // replaces/augments the base EVAS persona
    avatar: AvatarConfig,
    brain: BrainConfig,
    greeting: z.string().optional(), // overrides the default greeting line
    noNameGreeting: NoNameGreeting.optional(), // EVAS-461: a visitor with no name; unset = DEFAULT_NO_NAME_GREETING
    language: z.string().optional(), // BCP-47; overrides formData.language for the agent voice
    postHandoff: PostHandoffConfig.optional(), // manager-join reaction (defaults: introduce, keep avatar)
    agentAddressName: z.string().optional(), // wake name; once a manager is present the agent answers
    // voice only when addressed by this name (defaults to the AI Agent displayName at dispatch).
    noManagerTimeoutS: z.number().int().positive().optional(), // announce + end if no manager joins in time
    report: ReportConfig.optional(), // end-of-session report LLM (defaults: vertex / Gemini 3.1 Pro)
    // The post-handoff ADDRESSEE judge: once a manager joins, decides per human turn whether it is
    // addressed to the assistant. It sits between that turn and the reply, so it wants a fast model.
    // Until EVAS-409 this one setting also drove the identification judge below; a stored `judge` keeps
    // its meaning for the addressee judge and no longer reaches identification.
    judge: LlmConfig.optional(),
    // The VISITOR-IDENTIFICATION judge (EVAS-409): links the session to an existing Participant or
    // creates one, in the background after the greeting. Unset → its own default
    // (LLM_DEFAULTS.identityJudge), never the `judge` setting above.
    identityJudge: LlmConfig.optional(),
    // P5.4 private manager↔agent chat: the TEXT-ONLY reply LLM (default vertex / the FLASH tier — see
    // LLM_DEFAULTS, deliberately not named here so a tier bump cannot leave this line stale). A
    // separate non-voice path is structural, not a preference — a voice Realtime `generateReply` ALWAYS
    // speaks, so it cannot answer the manager without the visitor hearing it. This model is fed the same
    // persona + live transcript + visitor context the voice agent has, so it answers as the same agent.
    privateChat: LlmConfig.optional(),
    // ---- LeadVal integration (EVAS-136; docs/leadval/03 §5-bis, 02 §4 O4) ----
    identity: IdentityConfig.optional(),
    /** Per-AiAgent LeadVal knobs; effective only when the company is connected, harmless otherwise.
     * `attachMcpTools` auto-attaches the LeadVal MCP toolset (routing/read tools + the demo handoff
     * prompt) to the brain. */
    leadval: z
        .object({
        processor: LeadvalProcessor.default(LEADVAL_DEFAULT_PROCESSOR),
        attachMcpTools: z.boolean().default(true),
    })
        .optional(),
});
// ---------- Vertex AI (auxiliary Gemini tasks: participant identification, report generation) ----------
// These are the non-realtime LLM calls (NOT the avatar brain). Defaults are the single source of truth
// shared by agent-core (in-call judges) and the report-generator fn (P6). The newest models are served
// ONLY on the global endpoint, so the default location is "global". Models are env-overridable per task
// (VERTEX_MODEL for the judges, VERTEX_REPORT_MODEL for reports); endpoint via VERTEX_LOCATION.
// Pure constants only — env resolution lives in the Node consumers (agent-core, functions) so this
// module stays browser-safe.
// Tiering (Google's guidance after the *-latest alias discontinuation):
//   PRO   → complex tasks (end-of-session reports)
//   FLASH → medium tasks
//   LITE  → simple/high-volume tasks (per-utterance judges: addressee, gating, participant ID)
export const VERTEX_DEFAULT_LOCATION = "global";
export const VERTEX_DEFAULT_FLASH_MODEL = "gemini-3.8-flash"; // GA 2026-09
// EVAS-410: Gemini 4 Argon, generally available on Vertex since 2026-10-13, replaces gemini-3.1-pro-preview
// (still a preview, with no stability commitment) as the tier behind the end-of-session report. Rows that
// pin 3.1 Pro explicitly keep it: it is still served and stays selectable.
export const VERTEX_DEFAULT_PRO_MODEL = "gemini-4-argon";
export const VERTEX_DEFAULT_LITE_MODEL = "gemini-3.5-flash-lite"; // GA 2026-07
// Current Vertex Claude tier ids (EVAS-183: the 5.5 family replaced Haiku 4.5 / Sonnet 5 / Opus 5).
// Bedrock has its own constant below (BEDROCK_CLAUDE): its ids differ and its Haiku lags.
const VERTEX_CLAUDE = {
    haiku: "claude-haiku-5-5",
    sonnet: "claude-sonnet-5-5",
    opus: "claude-opus-5-5",
};
// Discontinued/rolling/superseded Vertex model ids → current pinned equivalents.
// Applied wherever a model id enters from config/env/DB (createVertexClient, report-generator), so a
// stale AiAgent.params row or env var never reaches the API with a dead model id, and rows persisted
// with a superseded tier model silently ride the tier upgrade (EVAS-99).
export const DEPRECATED_VERTEX_MODEL_ALIASES = {
    "gemini-flash-latest": VERTEX_DEFAULT_FLASH_MODEL,
    "gemini-flash-lite-latest": VERTEX_DEFAULT_LITE_MODEL,
    "gemini-pro-latest": VERTEX_DEFAULT_PRO_MODEL,
    // superseded tier models (EVAS-99, GA upgrades 2026-07; EVAS-126, flash → 3.7 GA 2026-08;
    // EVAS-180, flash → 3.8 GA 2026-09). Each hop points at the CONSTANT, never at the previous id:
    // normalizeVertexModel does a single lookup, so a chain would resolve to a dead model.
    "gemini-3.7-flash": VERTEX_DEFAULT_FLASH_MODEL,
    "gemini-3.6-flash": VERTEX_DEFAULT_FLASH_MODEL,
    "gemini-3.5-flash": VERTEX_DEFAULT_FLASH_MODEL,
    "gemini-3.1-flash-lite": VERTEX_DEFAULT_LITE_MODEL,
    // EVAS-96: MaaS ids must be <publisher>/<model>; the catalog briefly shipped the bare id.
    "gpt-oss-120b-maas": "openai/gpt-oss-120b-maas",
    // EVAS-183: Claude tier bump on Vertex. Same one-hop rule: point at VERTEX_CLAUDE, and a later
    // bump edits those values and adds the outgoing id as a new key. The bare `claude-haiku-4-5`
    // never was a catalog id, but an env override could carry it.
    "claude-haiku-4-5@20251001": VERTEX_CLAUDE.haiku,
    "claude-haiku-4-5": VERTEX_CLAUDE.haiku,
    "claude-sonnet-5": VERTEX_CLAUDE.sonnet,
    "claude-opus-5": VERTEX_CLAUDE.opus,
};
/** Map a discontinued Vertex model id to its current pinned model; pass anything else through. */
export function normalizeVertexModel(model) {
    return DEPRECATED_VERTEX_MODEL_ALIASES[model] ?? model;
}
// Current Bedrock Claude tier ids (EVAS-310: Sonnet/Opus moved to 5.5 once @ai-sdk/amazon-bedrock
// stopped forcing a JSON tool on them; Sonnet 5.5 is unverified on evas-dev, which AWS billing still
// refuses, see its catalog $comment). Haiku stays on 4.5 until the evas-dev AWS account can open the
// Haiku 5.5 Marketplace subscription (EVAS-311); that bump edits `haiku` and adds the 4.5 id as an
// alias key, by the same one-hop rule as the Vertex map.
const BEDROCK_CLAUDE = {
    haiku: "global.anthropic.claude-haiku-4-5-20251001-v1:0",
    sonnet: "global.anthropic.claude-sonnet-5-5",
    opus: "global.anthropic.claude-opus-5-5",
};
// Per-provider deprecated-model alias maps (EVAS-96 generalization of the Vertex map above). Applied
// wherever a model id enters from config/env/DB so stale rows ride upgrades instead of failing.
// Exported for the catalog invariant test, which checks every provider's targets (EVAS-387).
export const DEPRECATED_MODEL_ALIASES = {
    vertex: DEPRECATED_VERTEX_MODEL_ALIASES,
    openai: {
        // EVAS-387: OpenAI shuts gpt-5.4-nano down on 2027-04-01 and names gpt-6-luna as its replacement.
        // Luna costs less ($0.10/$0.50 against $0.20/$1.25) and offers the same levels, none to xhigh, so
        // a stored reasoning level rides along unclamped.
        "gpt-5.4-nano": "gpt-6-luna",
    },
    bedrock: {
        // EVAS-310: the 5.0 ids, the only Bedrock Sonnet/Opus ids the catalog ever shipped (EVAS-96).
        "global.anthropic.claude-sonnet-5": BEDROCK_CLAUDE.sonnet,
        "global.anthropic.claude-opus-5": BEDROCK_CLAUDE.opus,
    },
    // EVAS-349: no stored row predates this provider, so nothing to alias yet. The first tier bump adds
    // the outgoing id here, by the one-hop rule of the maps above.
    anthropic: {},
};
/** Map a provider's discontinued model alias to its current pinned model; pass anything else through. */
export function normalizeModel(provider, model) {
    return DEPRECATED_MODEL_ALIASES[provider][model] ?? model;
}
/** Blended $/1M for sorting + banding: input-weighted 3:1 (structured judge/report calls are
 * prompt-heavy, output-light). */
export function llmBlendedPrice(e) {
    return (3 * e.pricing.input + e.pricing.output) / 4;
}
/** Price band `$`…`$$$$$` shown as a badge next to real prices in the console. Thresholds on the
 * blended price chosen so current models split into: $ ultra-cheap (gpt-oss, Luna, Haiku 5.5),
 * $$ (lite/mini/Haiku 4.5), $$$ (flash/Gemini 3.1 Pro/Sonnet/GPT-unsuffixed/GPT-6.1 Sol), $$$$ (Gemini 4
 * Argon/Opus/GPT-5.6 Sol), $$$$$ (Pro pricing — gpt-*-pro — none curated today). The $10/$50 frontier tier
 * (GPT-6 Astra, Claude Fable) blends to 20 and so lands in $$$$, not $$$$$. */
export function llmPriceBand(e) {
    const p = llmBlendedPrice(e);
    if (p < 0.5)
        return "$";
    if (p < 2.5)
        return "$$";
    if (p < 7)
        return "$$$";
    if (p < 25)
        return "$$$$";
    return "$$$$$";
}
// Per-use-case default LlmConfig when an AI Agent doesn't set one. Judges are high-volume/simple →
// lite tier; reports are complex one-per-session → pro tier (unchanged from P6). `privateChat` (P5.4)
// answers a manager's private question in TEXT — conversational, low-volume, manager-facing quality →
// medium/flash tier. `judge` is the post-handoff addressee judge and `identityJudge` the
// visitor-identification judge (EVAS-409: one setting drove both before). They start on the same
// tier, so splitting them changed no model; each can now move on its own.
export const LLM_DEFAULTS = {
    judge: { provider: "vertex", model: VERTEX_DEFAULT_LITE_MODEL },
    identityJudge: { provider: "vertex", model: VERTEX_DEFAULT_LITE_MODEL },
    report: { provider: "vertex", model: VERTEX_DEFAULT_PRO_MODEL },
    privateChat: { provider: "vertex", model: VERTEX_DEFAULT_FLASH_MODEL },
};
// Structured report body produced by the LLM, stored as the `.json` artifact and rendered to the `.md`.
// Shared by the report-generator fn (structured-output schema) and the console (typed render). `summary`
// is also persisted onto the Report row for list previews.
export const ReportContent = z.object({
    summary: z.string(),
    participantIntent: z.string(),
    sentiment: z.string(),
    actionItems: z.array(z.string()),
    handoffOutcome: z.string(),
    followUps: z.array(z.string()),
});
// ---------- widget host-page postMessage API ----------
// The widget posts events to the host page's window; the host page subscribes through `window.EVAS('on',
// handler)`. Both travel as a postMessage envelope marked with WIDGET_HOST_ENVELOPE, and every inbound
// frame is PARSED against these schemas, never cast (EVAS-275): a "message" listener receives whatever any
// script on the page sends, and a malformed frame must be dropped, not handed to a host's handler.
export const WIDGET_HOST_ENVELOPE = "evas-widget";
export const WidgetHostEvent = z.discriminatedUnion("type", [
    z.object({ type: z.literal("evas:ready"), embeddingId: z.string() }),
    z.object({ type: z.literal("evas:sessionStart"), sessionId: z.string() }),
    z.object({ type: z.literal("evas:sessionEnd"), sessionId: z.string() }),
    // `payload` optional: an undefined property survives structured clone but not JSON, and a host or a
    // bridge in between may serialize.
    z.object({
        type: z.literal("evas:agentEvent"),
        name: z.string(),
        payload: z.unknown().optional(),
    }),
    z.object({ type: z.literal("evas:managerJoined"), mode: ContactMode }),
    z.object({ type: z.literal("evas:error"), code: z.string(), message: z.string() }),
]);
/** The command half of the API. The wire shape is fixed here; nothing sends or receives it yet (the
 * widget's `onCommand` was removed as dead code in EVAS-275 — wiring it is a design of its own: closing
 * during a live session, what `setContext` feeds, and a `window.EVAS` verb for the host to send it). */
export const WidgetHostCommand = z.discriminatedUnion("type", [
    z.object({ type: z.literal("evas:open") }),
    z.object({ type: z.literal("evas:close") }),
    z.object({ type: z.literal("evas:setContext"), payload: z.record(z.string(), z.unknown()) }),
]);
/** What actually crosses `postMessage`, in each direction. */
export const WidgetHostEventMessage = z.object({
    __evas: z.literal(WIDGET_HOST_ENVELOPE),
    event: WidgetHostEvent,
});
export const WidgetHostCommandMessage = z.object({
    __evas: z.literal(WIDGET_HOST_ENVELOPE),
    command: WidgetHostCommand,
});
// ---------- agent <-> functions ----------
export const ManagerDispatchRequest = z.object({
    v: z.literal(1),
    sessionId: z.string(),
    roomName: z.string(),
    reason: z.string().max(500),
});
/**
 * The manager-dispatch function's reply (EVAS-231). Lived as an ad-hoc object on both sides until the two
 * sides were found writing the same `MANAGER_REQUESTED` event — one row each, 57 ms apart — because neither
 * knew what the other recorded.
 *
 * ⚠ `recorded` IS THE SINGLE-WRITER HANDSHAKE. The function performed the side effect (email / dial / PIN
 * rule) and is idempotent per (session, mode), so it owns the record: it writes state + event after the
 * dispatch, BEST-EFFORT — a database failure there is logged and must never turn a delivered invite into a
 * non-2xx that the agent reads as "not reached". `recorded: false` tells the agent the write did not happen,
 * and only then does the tool write the row itself. Absent (an older function during a rolling deploy)
 * means the function wrote, as every earlier version did before replying.
 */
export const ManagerDispatchResponse = z.object({
    mode: ContactMode,
    /** True when the function persisted state + event; false when that failed after a successful dispatch. */
    recorded: z.boolean().optional(),
    /** PSTN_INBOUND_PIN only — the per-session dispatch rule the agent deletes at session end. */
    dispatchRuleId: z.string().optional(),
    /** Set on the idempotent replay path: nothing was re-dispatched. */
    status: z.literal("already-dispatched").optional(),
});
export const ReportJobRequest = z.object({ v: z.literal(1), sessionId: z.string() });
// ---------- manager-room <-> agent in-room control (P5.1 §C) ----------
/** Text-stream topic the manager-room publishes control commands on (distinct from `lk.chat` and
 * `lk.transcription`). The agent owns this topic and acts only on commands from a manager participant. */
export const ROOM_CONTROL_TOPIC = "evas.control";
/** A manager-initiated in-room control command. Versioned + a single `type` literal today so the union
 * can grow (e.g. more controls) without breaking the wire. `closeAvatar` tears the LiveAvatar down while
 * the agent stays connected and keeps transcribing (mirrors the `postHandoff.closeAvatar` toggle). */
export const RoomControlCommand = z.object({ v: z.literal(1), type: z.literal("closeAvatar") });
// ---------- client → agent audio-capture telemetry (EVAS-216) ----------
/** Text-stream topic a CLIENT publishes facts about its own capture pipeline on. Separate from
 * `ROOM_CONTROL_TOPIC` deliberately: that one is manager-gated and carries commands, this one is
 * visitor-sourced and carries observations, and conflating the two would mean one gate for both. */
export const CLIENT_TELEMETRY_TOPIC = "evas.client-telemetry";
/** Text-stream topic the AGENT confirms a client telemetry message on, to its sender only (EVAS-284).
 *
 * `sendText` resolves once the message is queued in the sender's data channel, not once anyone has read
 * it, and leaving the room goes over the signalling socket, which can overtake the data. Measured on dev
 * 2026-10-13: 3 of 7 sessions ended with End lost the end-of-session `AUDIO_STATS` sample, and the agent
 * logged the stream as cut off by the visitor's departure. The widget waits for this confirmation, with a
 * short bound, before it leaves. See {@link ClientTelemetryAck}. */
export const CLIENT_TELEMETRY_ACK_TOPIC = "evas.client-telemetry.ack";
/** Data-channel topic the agent publishes a per-agent event on (`agent.ts`, `emitToWidget`); the host
 * page's event of the same name is what the widget would post onward. One name, pinned by the wire
 * fixtures (EVAS-279), rather than a literal in the publisher. */
export const AGENT_EVENT_TOPIC = "evas:agentEvent";
/**
 * WHAT THE BROWSER ACTUALLY DID TO THE MICROPHONE, as opposed to what we asked it to do.
 *
 * `audioCaptureDefaults` *requests* echo cancellation, noise suppression, gain control and voice
 * isolation. A request is not a guarantee — the browser may ignore or degrade any of them, and there are
 * real mechanisms for it (playback on a non-default output device, high-latency Bluetooth defeating the
 * canceller's delay estimation, a separate USB mic and built-in speakers on independent clocks, a
 * driver-level "system AEC" that is a no-op on that hardware). Only `getSettings()` on the live track
 * knows, and only the client can read it.
 *
 * This existed as an unanswerable question throughout EVAS-211: whether the 12.2 phantom detections per
 * minute were residual echo surviving a working canceller, or echo arriving because no canceller ran.
 * Those two have the same symptom and completely different fixes, and settling it took a developer
 * opening `chrome://webrtc-internals` by hand at the right moment and describing four live graphs.
 *
 * Every field is optional because a browser may report none of them, and an absent key means "not
 * reported" — never "false". Fabricating a default here would turn a missing measurement into a
 * confident wrong one.
 */
export const ClientAudioCapture = z.object({
    /** From `getSettings()` — what was APPLIED, not what was asked for. */
    echoCancellation: z.boolean().optional(),
    noiseSuppression: z.boolean().optional(),
    autoGainControl: z.boolean().optional(),
    /** Browser/OS voice isolation (Apple's on macOS). Requested on every call by `livekit-client`'s own
     * `audioDefaults` — not by us — so whether it does anything is a per-platform unknown until read back. */
    voiceIsolation: z.boolean().optional(),
    sampleRate: z.number().int().positive().optional(),
    channelCount: z.number().int().positive().optional(),
    /** Seconds, as the spec defines it. Relevant because a long capture latency is one of the ways echo
     * cancellation degrades. */
    latency: z.number().nonnegative().optional(),
    /** Constraint names the browser does not advertise at all (`getSupportedConstraints()`). Only the
     * NEGATIVES are carried: they are what distinguishes "requested and refused" from "requested and
     * silently unsupported", which is the distinction that makes a missing `voiceIsolation` readable. */
    unsupported: z.array(z.string().max(40)).max(8).optional(),
    /** Flags this session deliberately requested OFF (the field-test override). Present so a session's
     * numbers can never be compared against the wrong arm of an A/B. */
    disabled: z.array(z.string().max(40)).max(8).optional(),
    /**
     * Which constraints the browser DOES advertise (`getSupportedConstraints()`), not only the negatives.
     *
     * Added after the first version of this feature recorded nothing at all. In an engine that omits the DSP
     * flags from `getSettings()`, this is the only signal left — and it still separates "the platform has no
     * such feature" from "the platform has it and would not say what it did".
     */
    supported: z.array(z.string().max(40)).max(8).optional(),
    /**
     * Coarse engine class — `chromium` | `gecko` | `webkit` | `unknown`. NOT a user-agent string: a UA is a
     * fingerprinting surface and far more than this needs.
     *
     * ⚠ IT EXISTS BECAUSE THE ANSWER TURNED OUT TO BE ENGINE-DEPENDENT AND NO ROW SAID WHICH ENGINE PRODUCED
     * IT. Comparing "Firefox reported nothing" with "Edge reported stats" required remembering which browser
     * each test session used, which is not a thing a table should require.
     */
    engine: z.enum(["chromium", "gecko", "webkit", "unknown"]).optional(),
    /**
     * Why this report carries no measurements, when it carries none.
     *
     * THE POINT OF THE WHOLE FIX. The first version suppressed an empty report entirely, so "the browser told
     * us nothing", "we never got a track" and "the agent rejected the payload" were one indistinguishable
     * silence — in a feature built to stop exactly that kind of guessing. An event that says `no-settings` is
     * a finding; a missing event is a blind spot.
     */
    empty: z.enum(["no-track", "no-settings"]).optional(),
});
/**
 * HOW MUCH ECHO THE CANCELLER ACTUALLY REMOVED, sampled once at the end of the session (EVAS-216).
 *
 * `ClientAudioCapture` answers "was echo cancellation applied?" — a boolean. This answers "and did it
 * work?", which is the question that actually decides what to do next, because those two are
 * independent: a canceller can be fully applied and remove almost nothing.
 *
 * Measured by hand on 2026-09-19 (dev, laptop mic array + built-in speakers): echo cancellation applied,
 * `echoReturnLossEnhancement` topping out near *4 dB* where a well-coupled canceller achieves 20–40. That
 * single number is the difference between "tune the VAD threshold" and "buy voice isolation", and reading
 * it cost a developer a live `chrome://webrtc-internals` session and a round-trip of description. These
 * come from the standard `media-source` stats (kind=audio) via LiveKit's public
 * `Track.getRTCStatsReport()`.
 *
 * A DELIBERATELY TINY, HAND-PICKED SET. `getStats()` returns dozens of dictionaries including ICE
 * candidate IP addresses; dumping it into a lean high-write table would be both a privacy problem and the
 * thing AGENTS.md §2 warns against. The broader "intercept everything and alert on it" product is a
 * separate piece of work.
 */
export const ClientAudioStats = z.object({
    /** dB. CAN BE NEGATIVE — a negative ERL means the mic picks up the speaker output louder than the
     * reference signal, i.e. very tight acoustic coupling. Do not constrain this to positives. */
    echoReturnLoss: z.number().optional(),
    /** dB removed by the canceller. The headline number: ~20–40 is healthy, single digits is not. */
    echoReturnLossEnhancement: z.number().optional(),
    /** Cumulative since capture started; divide by {@link totalSamplesDuration} to compare sessions of
     * different lengths. Stored raw rather than pre-divided so the arithmetic stays checkable. */
    totalAudioEnergy: z.number().nonnegative().optional(),
    /** Seconds of audio captured. The denominator, and a sanity check that the sample is not from a session
     * that barely started. */
    totalSamplesDuration: z.number().nonnegative().optional(),
    /** Instantaneous level at sample time (0–1). Weak on its own, cheap to carry, occasionally the tell. */
    audioLevel: z.number().min(0).max(1).optional(),
    /** Coarse engine class, for the same reason {@link ClientAudioCapture} carries one: the answer is
     * engine-dependent (see `empty`) and a row that does not say which engine produced it cannot be read. */
    engine: z.enum(["chromium", "gecko", "webkit", "unknown"]).optional(),
    /**
     * Why this sample carries no numbers, when it carries none.
     *
     * ⚠ `no-audio-source` IS A PERMANENT BROWSER CAPABILITY GAP, NOT A FAILURE — and it is the reason this
     * field exists. Firefox's `dom/webidl/RTCStatsReport.webidl` declares `RTCMediaSourceStats`
     * (`trackIdentifier`, `kind`) and `RTCVideoSourceStats`, but **no `RTCAudioSourceStats`**: there is no
     * `audioLevel`, `totalAudioEnergy`, `echoReturnLoss` or `echoReturnLossEnhancement` for a LOCAL
     * microphone anywhere in Gecko. `"media-source"` is in its `RTCStatsType` enum, so the entry is found and
     * then turns out to be empty — which is why suppressing empty samples made Gecko look identical to a
     * broken send. Gecko does implement those three energy fields on `RTCInboundRtpStreamStats`, i.e. for
     * RECEIVED audio only: the mirror image of what this measurement needs.
     *
     * No web API recovers ERL/ERLE there — they are the echo canceller's own internals, and only the browser
     * that runs the canceller can report them. Mic ENERGY alone is recoverable cross-browser via Web Audio
     * (`AudioContext` + `AnalyserNode`), which is a separate piece of work and not a substitute.
     */
    empty: z.enum(["no-track", "no-report", "no-audio-source"]).optional(),
    /**
     * The same three level fields, DERIVED FROM WEB AUDIO rather than reported by `getStats()` (EVAS-216).
     *
     * ⚠ A SEPARATE FIELD, NOT A FALLBACK MERGED INTO THE ONES ABOVE, and that is the whole design. Writing our
     * number into `totalAudioEnergy` would make two different kinds of number indistinguishable in one column,
     * which is the exact failure this whole issue is about — and they ARE different kinds, measurably:
     *
     * ⚠ WEB AUDIO IS HANDED A QUIETER SIGNAL THAN CHROMIUM'S ENCODER REPORTS, BY A PER-SESSION CONSTANT (~8×
     * on 2026-09-22; 7–20× across other sessions). Settled by an every-sample `AudioWorklet` integral agreeing
     * with the polled windows to four decimals while both sat 8× under the native figure: nothing was lost in
     * measurement, the signal itself differs, and the boundary is inside Chromium. So this block is NOT on
     * Chromium's scale and must never be compared with the native fields — and since Chromium HAS native
     * fields, the meter no longer runs there at all (`meterMicrophone` in widget-core). It runs where the native
     * numbers do not exist — Gecko today — and there it is the only evidence of whether the microphone
     * captured speech, and roughly how much, consistently across sessions in that engine.
     *
     * `echoReturnLoss`/`echoReturnLossEnhancement` have no counterpart here and never will — they compare the
     * mic against the echo canceller's internal reference signal, which nothing outside the browser's DSP can
     * see. In Gecko, cancellation effectiveness is unmeasurable, full stop.
     */
    webAudio: z
        .object({
        /** 0–1 RMS of the most recent window. Keeps the spec's name because it IS the same quantity — the
         * spec leaves the averaging interval implementation-defined. */
        audioLevel: z.number().min(0).max(1).optional(),
        /**
         * The spec's energy formula — `Σ duration × (x/max)²` — over the signal Web Audio receives. By
         * `method`: `integral` is the exact per-sample sum from an `AudioWorklet`; `windows` is the same formula
         * over 250 ms analyser windows (the fallback where the worklet cannot load, e.g. a customer CSP that
         * blocks `blob:` workers). The two agreed to four decimals when run side by side, which is why one
         * field with a method tag replaced two blocks. Divide by {@link observedSeconds} to compare sessions.
         */
        energy: z.number().nonnegative().optional(),
        /**
         * ⚠ DELIBERATELY NOT CALLED `totalSamplesDuration`, because it is not that quantity. The spec's field
         * counts the duration of samples that reached the media source, so it stops advancing while the
         * visitor is muted; this is how long WE were listening, on the `AudioContext` clock. Close in the
         * common case, provably different under mute — and naming ours after theirs would assert an
         * equivalence that does not hold, in a payload where both sit side by side.
         */
        observedSeconds: z.number().nonnegative().optional(),
        /** Worklet messages (`integral`, one per ~250 ms) or analyser windows (`windows`) folded in. Two and
         * forty are both "measured"; only this separates them. */
        samples: z.number().int().nonnegative().optional(),
        /** Which estimator produced `energy`/`observedSeconds`/`samples`. Absent on rows written before the
         * tag existed (2026-09-22 and earlier), which were all `windows`. */
        method: z.enum(["integral", "windows"]).optional(),
    })
        .optional(),
});
/** A message on {@link CLIENT_TELEMETRY_TOPIC}. Versioned and a discriminated union so the wire can grow
 * (e.g. output-device facts, connection-quality history) without breaking. */
// ---------- session lifecycle timings (EVAS-222) ----------
/** Max named spans in one timing record. A diagnostic, not a trace stream: `SessionEvent` is a lean
 * high-write table (AGENTS.md §2) and an unbounded map is how one becomes a sink. */
export const TIMING_SPANS_MAX = 24;
/**
 * WHO WE WERE WAITING FOR — one bounded record of named durations per stage of a session (EVAS-222).
 *
 * The problem this exists for: the avatar greets after 1–2 s in some sessions and ~10 s in others, and
 * nothing said which step was slow, so "is it us, OpenAI, LiveKit or the avatar vendor" could not be
 * answered. The join function had the same gap in a harder form — on 2026-09-19 two joins died at exactly
 * the Cloud Run 60 s timeout and the logs named no step, so the cause had to be recovered from Cloud
 * Monitoring instead (CPU p99 at 1–2%, i.e. a throttled background warm-up).
 *
 * `spans` is a free map ON PURPOSE. The set of interesting stages is still being learned, and a typed
 * field per stage would mean a contract change every time we look somewhere new — the bound is on size,
 * not on vocabulary. Span names should stay stable once used, because they are what a query groups by.
 *
 * ⚠ RESOURCE CONTEXT IS NOT OPTIONAL DECORATION. A request that took 60 s of wall time and 200 ms of CPU
 * is a categorically different bug from one that burned a core, and durations alone cannot tell them
 * apart — that distinction is exactly what cost a day of investigation. Hence `cpuMs` and `loopLagMs`,
 * and `cold`/`depsReady` to say whether the instance was still warming when the work arrived.
 */
export const SessionTimings = z.object({
    /** Which side measured it. Three sides see three different halves of the same wait. */
    scope: z.enum(["join", "agent", "client"]),
    /** Span name → milliseconds. Bounded in count and per-value so a bug cannot write a huge row. */
    spans: z
        .record(z.string().min(1).max(40), z.number().nonnegative().max(3_600_000))
        .refine((v) => Object.keys(v).length <= TIMING_SPANS_MAX, {
        message: `at most ${TIMING_SPANS_MAX} spans`,
    }),
    /** Wall-clock total for the scope, when it is more than the sum of the named spans. */
    totalMs: z.number().nonnegative().max(3_600_000).optional(),
    /**
     * First request served by this instance/process — cold behaviour is measurably different.
     *
     * ⚠ ONLY MEANINGFUL WHERE ONE PROCESS SERVES MANY REQUESTS (the join function). The agent deliberately
     * omits it: LiveKit gives each job its own prewarmed child process that handles exactly one job, so
     * "first report in this process" is always true there and says nothing. Measured 2026-09-19 — three
     * sessions reported `cold: true` with process ages of 38–47 minutes.
     */
    cold: z.boolean().optional(),
    /** How long this process had been alive. In the agent that is the PREWARM age of the job's child
     * process — how long it idled before the job arrived — not a cold/warm signal. */
    instanceAgeMs: z.number().nonnegative().optional(),
    /** CPU time consumed during the scope (`process.cpuUsage()` delta). See the warning above. */
    cpuMs: z.number().nonnegative().optional(),
    /** Event-loop delay sample. Large lag on small CPU is the signature of a throttled container. */
    loopLagMs: z.number().nonnegative().optional(),
    /** Background dependencies ready / expected when the work started (e.g. geo datasets loaded of 8).
     * One number that would have replaced a whole metrics investigation. */
    depsReady: z.number().int().nonnegative().max(1000).optional(),
    depsExpected: z.number().int().nonnegative().max(1000).optional(),
});
export const ClientTelemetry = z.discriminatedUnion("type", [
    z.object({ v: z.literal(1), type: z.literal("audioCapture"), audio: ClientAudioCapture }),
    z.object({ v: z.literal(1), type: z.literal("audioStats"), stats: ClientAudioStats }),
    /** EVAS-222: the client half of the wait — it is the only side that knows when the black screen ended. */
    z.object({ v: z.literal(1), type: z.literal("timings"), timings: SessionTimings }),
]);
/**
 * The agent read a whole message on {@link CLIENT_TELEMETRY_TOPIC} (EVAS-284), sent on
 * {@link CLIENT_TELEMETRY_ACK_TOPIC} to that message's sender only.
 *
 * `streamId` is the id the sender's `sendText` returned for the message. It confirms delivery, not
 * acceptance: a message the agent read and then rejected, or did not store because of the per-session
 * cap, is confirmed too, because what the sender waits for is "safe to leave the room", and a rejected
 * message is not made any better by the sender staying.
 */
export const ClientTelemetryAck = z.object({
    v: z.literal(1),
    type: z.literal("ack"),
    streamId: z.string().min(1).max(128),
});
/**
 * Collect named spans without threading a stopwatch through every call site.
 *
 * `mark(name)` records the time since the previous mark (or since construction), so instrumenting a
 * sequence of awaits is one line per await and the numbers cannot drift out of step with the code. Pure
 * and dependency-free so the join function, the agent and the widget all use the same one — three
 * implementations of "measure a span" would produce three subtly different definitions of "total".
 */
export function createSpanTimer(now = () => Date.now()) {
    const start = now();
    let last = start;
    const spans = {};
    return {
        mark(name) {
            const at = now();
            // Later marks with the same name accumulate rather than overwrite: a retried step is one span.
            spans[name] = (spans[name] ?? 0) + Math.max(0, at - last);
            last = at;
        },
        elapsed() {
            return Math.max(0, now() - start);
        },
        spans() {
            return { ...spans };
        },
    };
}
// ---------- chat attachments (images & files) — EVAS-15 / P5.5 ----------
// Binary attachments ride LiveKit **byte streams** on the SAME `lk.chat` topic as the text they belong
// to: `sendText(body, { topic: 'lk.chat', attachments: [File, …] })` writes the text stream with an
// `attachedStreamIds` header and then one byte stream per file, each carrying that stream id. Receivers
// register a byte-stream handler on `lk.chat` and pair each byte stream to its text message by id.
// (`sendFile` is the single-file primitive underneath — `sendText` calls it per attachment, which is what
// gives us the text+files-in-one-message semantics the chat UIs need. Byte-stream chunking, ordering and
// reassembly are the SDK's job; see docs/plans/PLAN_P5_5_chat_images_files.md.)
//
// Limits are decided HERE (build time, per the phase's acceptance criteria) and shared by every surface —
// the widget, the manager-room and the agent all enforce the same numbers, so a file that one side accepts
// can never be rejected by another. Data channels are not a file service: streams are only delivered to
// participants connected at send time, nothing is persisted, and every byte crosses the SFU — hence the
// deliberately small per-file cap.
/** Max size of a single chat attachment (5 MB). Small on purpose: attachments cross the data channel to
 * every participant and are not persisted anywhere. */
export const CHAT_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
/** Max attachments per chat message. */
export const CHAT_ATTACHMENT_MAX_COUNT = 3;
/** Max CHARACTERS of a single text attachment handed to the brain (EVAS-110) — deliberately a *different*
 * limit from {@link CHAT_ATTACHMENT_MAX_BYTES}, which bounds what crosses the data channel: 5 MB of CSV is
 * ~1.5M tokens, orders of magnitude past any context window and a real cost incident.
 *
 * Sized against the brain's actual window rather than the sender's patience. 12,000 characters is ~3k
 * tokens of prose and ~4k of dense CSV (structured text tokenizes worse than prose). That fits a page of
 * notes or a real spreadsheet extract while leaving room for the persona, the session's audio turns, and
 * up to `MAX_CONTEXT_IMAGES` images. Content past the cap is truncated with an explicit marker, so the
 * model knows it is looking at only part of a file.
 *
 * EVAS-122 moved the default brain from `gpt-realtime` (32,000-token window) to
 * {@link REALTIME_DEFAULT_MODEL} (128,000), which makes these caps ~4x more conservative than when they
 * were chosen. They are deliberately UNCHANGED: raising them is a cost decision (every character
 * re-serializes into every later request of the session), not a window-size one, and the smaller
 * `gpt-realtime-1.5` / `gpt-realtime-mini` are still selectable on 32k. */
export const CHAT_TEXT_ATTACHMENT_MAX_CHARS = 12_000;
/** Combined per-session character budget for text attachments in the brain context. The per-file cap
 * alone is defeated by volume (3 files per message, any number of messages) — the same unbounded growth
 * `MAX_CONTEXT_IMAGES` exists to stop, and worse here because every character re-serializes into every
 * later request. 24,000 characters ≈ two full-size files ≈ ~8k tokens worst case; past it the model is
 * told in words that it could not read the file rather than being silently short-changed. */
export const CHAT_TEXT_ATTACHMENT_SESSION_MAX_CHARS = 24_000;
/** Image MIME types accepted in chat. These are exactly the types the OpenAI Realtime brain can take as
 * `input_image` (the framework's `serializeImage` rejects anything else), so an accepted image can always
 * be handed to the model — see `packages/agent-core/src/attachments.ts`. */
export const CHAT_IMAGE_MIME_TYPES = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/gif",
];
/** Document types whose contents the brain CAN ingest, because a txt/csv simply *is* text: no new model
 * API surface is needed, just a strict UTF-8 decode and a user text message (EVAS-110, implemented in
 * `packages/agent-core/src/attachments.ts`). The remaining {@link CHAT_FILE_MIME_TYPES} — pdf, doc(x),
 * xls(x), ppt(x) — need real extraction (PDF parsing, OOXML unzip) and stay transcript-only. */
export const CHAT_TEXT_MIME_TYPES = ["text/plain", "text/csv"];
/** Non-image document types accepted in chat. Rendered as a download link by the chat UIs; of these only
 * {@link CHAT_TEXT_MIME_TYPES} are also fed to the brain.
 *
 * This list is the single gate every surface derives from — the two file pickers (via
 * {@link CHAT_ATTACHMENT_ACCEPT}), {@link validateChatAttachment} on send, and the agent's re-check on
 * receive — so a type is either sendable and receivable everywhere or nowhere. */
export const CHAT_FILE_MIME_TYPES = [
    "application/pdf",
    ...CHAT_TEXT_MIME_TYPES,
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    // PowerPoint (EVAS-118) — send/receive/download only, like pdf/doc(x)/xls(x). `vnd.ms-powerpoint` is
    // the legacy binary .ppt; the openxmlformats one is .pptx. Note a real deck often exceeds
    // CHAT_ATTACHMENT_MAX_BYTES — a known limitation of the data-channel transport, not of the allowlist.
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
];
/** Every MIME type accepted as a chat attachment (images + documents). */
export const CHAT_ATTACHMENT_MIME_TYPES = [
    ...CHAT_IMAGE_MIME_TYPES,
    ...CHAT_FILE_MIME_TYPES,
];
/** `accept` attribute for a chat file input — the allowlist above, verbatim. */
export const CHAT_ATTACHMENT_ACCEPT = CHAT_ATTACHMENT_MIME_TYPES.join(",");
/** Text-stream attribute carrying the attachment file names in SEND order, `\n`-separated.
 *
 * Why it exists: `@livekit/components-core`'s `setupChat` pairs byte streams to their message with
 * `mergeMap(attachments.values())` + `scan`, and **`mergeMap` emits in completion order** — so
 * `attachedFiles` is ordered by whichever file finished downloading first, not by the order the sender
 * picked them. Observed on dev: three images sent large→small arrived "second, third, first", and
 * image/pdf/image arrived "image2, image1, pdf". `concatMap` upstream would preserve order; until that
 * is fixed, the sender states the intended order here and receivers sort by it
 * (`orderAttachments`). Set by every EVAS sender; when absent (an older peer) receivers keep
 * arrival order. */
export const CHAT_ATTACHMENT_ORDER_ATTR = "evas.attachmentOrder";
/** Encode file names for {@link CHAT_ATTACHMENT_ORDER_ATTR}. Newline-separated because it cannot occur
 * in a filename on any platform we target, unlike commas. */
export function encodeAttachmentOrder(files) {
    return files.map((f) => f.name).join("\n");
}
/** Sort received attachments into the sender's order, using the {@link CHAT_ATTACHMENT_ORDER_ATTR}
 * attribute. Duplicate names are matched positionally (each name is consumed once), unlisted files keep
 * their relative arrival order and go last, and a missing/blank attribute returns the input untouched.
 * Pure — unit-tested; total (never drops or duplicates an entry). */
export function orderAttachments(files, order) {
    const names = (order ?? "").split("\n").filter((n) => n.length > 0);
    if (names.length === 0 || files.length <= 1)
        return [...files];
    const remaining = [...files];
    const sorted = [];
    for (const name of names) {
        const idx = remaining.findIndex((f) => f.name === name);
        if (idx >= 0)
            sorted.push(...remaining.splice(idx, 1));
    }
    return [...sorted, ...remaining]; // anything unlisted keeps arrival order, at the end
}
/** True when `mimeType` is an image we both render inline and can hand to the brain. */
export function isChatImageMimeType(mimeType) {
    return CHAT_IMAGE_MIME_TYPES.includes(mimeType.toLowerCase());
}
/** True when `mimeType` is a plain-text document whose decoded contents we hand to the brain (EVAS-110).
 * Distinct from {@link isChatImageMimeType}: both go to the model, by completely different paths (a
 * base64 `input_image` part vs a decoded text part). */
export function isChatTextMimeType(mimeType) {
    return CHAT_TEXT_MIME_TYPES.includes(mimeType.toLowerCase());
}
/** True when `mimeType` is accepted as a chat attachment at all. */
export function isChatAttachmentMimeType(mimeType) {
    return CHAT_ATTACHMENT_MIME_TYPES.includes(mimeType.toLowerCase());
}
/** Validate one candidate attachment against the shared limits. Pure — the widget (Preact), the
 * manager-room (React) and the agent (Node) all gate on this so the rules can't drift apart. */
export function validateChatAttachment(file) {
    if (!isChatAttachmentMimeType(file.type))
        return "type";
    if (file.size > CHAT_ATTACHMENT_MAX_BYTES || file.size <= 0)
        return "size";
    return null;
}
/** Human-readable byte size for UI labels ("824 KB", "1.4 MB"). */
export function formatFileSize(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
// ---------- private manager<->agent chat (EVAS-14 / P5.4) ----------
/** Text-stream topic for the PRIVATE manager↔agent exchange, separate from `lk.chat` (public chat,
 * broadcast to the room and answered ALOUD) and `evas.control`. A dedicated topic — not an attribute on
 * `lk.chat` — is what makes privacy verifiable on BOTH sides: the visitor's widget registers no handler
 * for it (so a private line can never surface in its caption panel even if the sender mis-targets), and
 * the agent knows a message is private from the topic alone. `reader.info` exposes no
 * `destinationIdentities`, so the receiving side cannot otherwise tell private from public.
 *
 * Both directions use this topic:
 *   manager → agent: `sendText(question, { topic, destinationIdentities: [<agentIdentity>] })`
 *   agent → manager: `sendText(answer,   { topic, destinationIdentities: [<managerIdentity>] })`
 * `destinationIdentities` keeps the packets off the visitor's client entirely (server-side routing);
 * the separate topic is the defense-in-depth layer on top. */
export const PRIVATE_CHAT_TOPIC = "evas.private-chat";
/** Wire cap for {@link PrivateChatMessage.text}. Named because it is enforced in three places that
 * must agree: the schema below (a longer frame fails `safeParse` and is dropped WHOLE, so the reader
 * sees nothing at all), and each sender, which truncates rather than lets that happen. */
export const PRIVATE_CHAT_MAX_TEXT = 4000;
/** What the agent sends a manager when it could not produce a private answer: the reply model failed or
 * ran past its bound (EVAS-341). Shared because the live harness has to tell it apart from a real answer:
 * a run that saw only this line did not get its answer, whatever the panel shows. */
export const PRIVATE_CHAT_FALLBACK_REPLY = "Sorry — I couldn't work that out just now. Could you ask me again?";
/** The manager room's own line when it could not send the question at all (apps/manager-room
 * PrivateChat.tsx). Shown as an agent line, but the agent never saw the question, so it is no answer. */
export const PRIVATE_CHAT_UNREACHABLE_NOTICE = "(couldn't reach the assistant — please try again)";
/** The manager room's own line when the agent left the call (or restarted as a new job) while a question
 * was unanswered: the answer can no longer arrive (EVAS-398). Shown as an agent line; no answer either. */
export const PRIVATE_CHAT_AGENT_LEFT_NOTICE = "(the assistant left the call before answering — ask again when it is back)";
/** What the agent sends a manager when the session has no private-reply model at all. Not an answer
 * either, for the same reason (EVAS-341). */
export const PRIVATE_CHAT_UNAVAILABLE_REPLY = "I can't answer privately right now — my private-reply model isn't configured for this session. Ask me in the public chat instead and I'll answer out loud.";
/** A message on {@link PRIVATE_CHAT_TOPIC}. Versioned so the wire can grow (e.g. threading, files).
 * `text` is the human-readable body; `direction` lets each side ignore its own echo without depending
 * on identity conventions. Both peers validate with this schema and drop anything that fails.
 *
 * `kind` (EVAS-117) marks a line the manager did NOT ask for: the auto-briefing pushed on join. The
 * manager-room needs to tell the two apart — an answer belongs in a panel the manager already opened,
 * an unsolicited briefing has to OPEN that panel or nobody reads it. Optional and additive, so it does
 * not bump `v`: zod strips unknown keys, so a pre-EVAS-117 peer parses the frame unchanged and renders
 * it as an ordinary answer.
 *
 * `.catch(undefined)` is the SKEW rule in the other direction, and the reason this is not a bare
 * `z.literal(...).optional()`: an unrecognized `kind` is a key that IS in the shape with a value that
 * fails it, which drops the WHOLE frame — a manager losing an entire answer because a newer agent
 * labeled it. Strict about what the value means, tolerant about not knowing it: an unknown kind is
 * discarded and the line is delivered plain. Peers here version independently (an agent redeploys
 * without the manager-room bundle in a manager's browser cache doing so). */
export const PrivateChatMessage = z.object({
    v: z.literal(1),
    direction: z.enum(["toAgent", "toManager"]),
    text: z.string().min(1).max(PRIVATE_CHAT_MAX_TEXT),
    kind: z.literal("briefing").optional().catch(undefined),
});
// ---------- transcript & session events ----------
export const Speaker = z.enum(["PARTICIPANT", "AGENT", "MANAGER"]);
export const TranscriptEvent = z.object({
    v: z.literal(1),
    sessionId: z.string(),
    ts: z.number(),
    speaker: Speaker,
    text: z.string(),
    raw: z.unknown().optional(),
});
export const SessionEventPayload = z.discriminatedUnion("type", [
    z.object({ type: z.literal("STATE_CHANGE"), from: SessionState, to: SessionState }),
    z.object({ type: z.literal("MANAGER_REQUESTED"), reason: z.string(), mode: ContactMode }),
    /**
     * A `request_manager` call that did NOT page anyone, and why (EVAS-218). `confirm` means the model was
     * told to ask the visitor and re-call with `confirmed: true`; `hold` means a manager was dispatched
     * within the last two minutes already. Written so a swallowed real request is discoverable rather than
     * invisible — the guard's fail-open promise is only checkable if every hold leaves a row.
     */
    z.object({
        type: z.literal("MANAGER_REQUEST_HELD"),
        outcome: z.enum(["confirm", "hold"]),
        why: z.enum(["uncertain-reason", "fragment-turn", "recent-dispatch"]),
        /** The model's own stated reason, as it wrote it — the evidence in the 2026-09-18 case. */
        toolReason: z.string().max(200),
        /** What the visitor had just said, when a recent turn was available. Transcript text, already stored. */
        visitorText: z.string().max(200).optional(),
    }),
    z.object({ type: z.literal("MANAGER_JOINED"), mode: ContactMode }),
    z.object({
        type: z.literal("PARTICIPANT_LINKED"),
        participantId: z.string(),
        /** How the link was decided — see {@link LinkMethod}. Until 2026-09-12 the deterministic email match
         * and the no-judge fallback both wrote `LLM`, which asserted a model had decided when none had. */
        method: LinkMethod,
    }),
    /**
     * One LeadVal identity lookup ran for this session (EVAS-140) — at most one row, written whether the
     * verdict was used or refused.
     *
     * Why it is recorded at all: the verdict decides whether the agent may greet by name and whether
     * personalization happens, and it is the ONLY trace of a `SESSION_FORK` — the impossible-travel
     * security signal on which the agent deliberately behaves normally in conversation (report 03 decision
     * Q4). Without a row, "the agent went cold on a returning customer" and "LeadVal told us not to trust
     * this session" look identical afterwards. Same reasoning as EVAS-172 for the ingestion verdict: a
     * verdict nobody persists is a verdict nobody can audit.
     */
    z.object({
        type: z.literal("IDENTITY_LOOKUP"),
        /** LeadVal's verdict verbatim — a STRING, not an enum: the vocabulary can grow, and a session event
         * is a record of what they said, not a place to validate it. */
        verdict: z.string(),
        /** The tuning profile the lookup was scored under (`resolutionProfileId`). Recorded because a
         * re-tune on their side changes what the same evidence means, with no deploy of ours to correlate. */
        resolutionProfileId: z.string().optional(),
        /** `matches[0].nameUse` as read, plus its reason. Absent when there were no matches. */
        nameUse: z.string().optional(),
        nameUseReason: z.string().optional(),
        /** Pointers we stored, for cross-referencing a session against LeadVal's own records. */
        leadId: z.string().optional(),
        customerProfileId: z.string().optional(),
        /** Did personalization actually reach the brain? `skipped` with a reason is the useful half. */
        personalization: z.enum(["applied", "skipped"]),
        /** ⚠ `applied` DOES NOT MEAN CONTENT ARRIVED — it means the verdict permitted a fetch. This says where
         * the content actually came from: the merged customer profile, the single lead, or `none` when a
         * confirmed match could not be read back. Absent when no fetch was attempted. Added after a dev session
         * (2026-09-12) recognised a returning visitor and nothing in any record could distinguish "we loaded
         * their history" from "we loaded nothing" — two very different conversations, identical telemetry. */
        content: z.enum(["profile", "lead", "none"]).optional(),
        /** Why personalization was skipped, or why the lookup contributed nothing: `session-fork`,
         * `profile-merge`, `no-match`, `name-withheld`, `rate-limited`, `timeout`, `not-enabled`,
         * `read-back-failed`, … Free text on purpose — it is a log line, not a branch. */
        reason: z.string().optional(),
    }),
    /**
     * THE LEADVAL INGESTION VERDICT — whether this session became a lead (EVAS-172).
     *
     * The whole `LeadvalPollResult`, because {@link Session}'s column carries only the one-value answer a LIST
     * question needs ("show me the failures") and everything that explains it belongs beside it: the missing
     * keys, the MANDATORY set their live registry held, the note codes. A rejection is TERMINAL and is never
     * reprocessed, so this row is the permanent record of a commercial loss — until this issue the verdict
     * existed only as a log line inside a LiveKit Cloud container.
     *
     * ⚠ Two fields here are NOT failures and must never render as one. `lastAwaitingMandatoryKeys` is the
     * PAST-TENSE wait record (EVAS-198): it survives the terminal write and is therefore present on
     * **CONFORMANT** sessions by design — it means "this session had to wait at least once", which on a
     * session that then succeeded is a leading indicator, not a fault. `awaitingMandatoryKeys` is the
     * present-tense diagnosis and means LeadVal is still waiting, not that it refused.
     */
    z.object({
        type: z.literal("LEADVAL_INGESTION"),
        outcome: LeadvalPollOutcome,
        /** `conformance.level` as reported. */
        level: z.string().optional(),
        /** `conformance.reason` — only on `NOT_EVALUATED`, and the reason to stop polling. */
        reason: z.string().optional(),
        processingState: z.string().optional(),
        /** Non-empty means the session produced NO lead and cannot be reprocessed. */
        missingMandatoryKeys: z.array(z.string()).optional(),
        /** Their live MANDATORY registry as it stood when this session was evaluated. Recorded, never branched
         * on: the registry widens with no deploy of theirs to correlate against. */
        mandatoryKeys: z.array(z.string()).optional(),
        /** ⚠ A DIAGNOSIS, NOT A VERDICT — LeadVal was still waiting and the keys usually arrive. */
        awaitingMandatoryKeys: z.array(z.string()).optional(),
        /** ⚠ PAST TENSE, present on CONFORMANT sessions by design. Not a failure. */
        lastAwaitingMandatoryKeys: z.array(z.string()).optional(),
        /** When the last wait was recorded. Re-stamped per re-read, so it is a timestamp, never a count. */
        lastAwaitedAt: z.string().optional(),
        noteCodes: z.array(z.string()).optional(),
    }),
    z.object({ type: z.literal("REPORT_REQUESTED") }),
    /**
     * WHAT THIS SESSION'S TRANSCRIPT DOES NOT CONTAIN (EVAS-189) — at most one row, at session close, and
     * only when something was actually dropped.
     *
     * A transcript that is quietly incomplete is worse than one that is visibly incomplete: without this
     * row, "the agent said little" and "the agent said plenty and we failed to record it" look identical
     * afterwards, and the second is the one that invalidates the end-of-session report and the LeadVal
     * document built from the same turns. `emptyText` is the count that matters — a turn whose role we do
     * record arriving with no text, most plausibly an utterance interrupted before its transcript arrived.
     *
     * Counts, not the dropped content: the content is precisely what we did not have. The agent log carries
     * per-item detail for the first few; this row carries the RATE, which is the operational question.
     */
    z.object({
        type: z.literal("TRANSCRIPT_DROPS"),
        /** A recorded role with nothing to record — the lossy one. */
        emptyText: z.number().int().nonnegative().optional(),
        /** A company `onTranscriptChunk` hook returned empty text. Deliberate, but still a missing turn. */
        hookDropped: z.number().int().nonnegative().optional(),
        /** By design, and counted only so the arithmetic closes: tool/system items, non-conversational roles,
         * and a manager turn already recorded by `chat.ts` under its true speaker. */
        notChatMessage: z.number().int().nonnegative().optional(),
        nonConversationalRole: z.number().int().nonnegative().optional(),
        claimedElsewhere: z.number().int().nonnegative().optional(),
    }),
    /** The visitor's applied microphone DSP settings (EVAS-216) — see {@link ClientAudioCapture} for why
     * this is worth a row. Derived from the same schema as the wire message so the two cannot drift. */
    ClientAudioCapture.extend({ type: z.literal("AUDIO_CAPTURE") }),
    /** How much echo the canceller removed, sampled at session end (EVAS-216) — see
     * {@link ClientAudioStats}. The companion to AUDIO_CAPTURE: that one says whether it ran, this one
     * whether it worked. */
    ClientAudioStats.extend({ type: z.literal("AUDIO_STATS") }),
    /** Named durations for one stage of the session (EVAS-222) — see {@link SessionTimings}. Up to three
     * rows per session, one per `scope`, because the client, the join function and the agent each see a
     * different part of the same wait and none of them can measure the others'. */
    SessionTimings.extend({ type: z.literal("TIMINGS") }),
    z.object({ type: z.literal("CUSTOM"), name: z.string(), payload: z.unknown() }),
]);
// ─────────────────────────── Data Connect id format (EVAS-137) ───────────────────────────
/**
 * Does this look like an id Data Connect produced?
 *
 * ⚠ Data Connect serializes its `UUID` scalar as **32 hex characters with NO dashes**
 * (`79d4a823ff1b4571bf24e0a0118cf503`), not the canonical 8-4-4-4-12 form. Route handlers that
 * validated path ids against a dashed regex therefore rejected every real id with a 400 — measured on
 * dev 2026-08-30, where it silently broke the company auth-settings write AND (same regex, copied) the
 * per-company IP-intelligence override from EVAS-138, which had never been savable in production.
 *
 * Both forms are accepted: the dashed one because nothing stops a future caller normalizing, and the
 * bare one because it is what the database actually returns. The value is passed through verbatim —
 * it came FROM Data Connect, so it is not ours to reformat.
 */
export function isDataConnectId(value) {
    return typeof value === "string" && DATACONNECT_ID_RE.test(value);
}
/**
 * Are these the same Data Connect id, whichever form each is written in?
 *
 * Needed because `isDataConnectId` accepts both the undashed form the database returns and the
 * canonical dashed one — so a raw `===` between an id from a URL and one from a custom claim can
 * disagree about the same row and produce a FALSE DENY (a legitimate company admin gets a 403).
 * Use this wherever the two sides have different provenance. Where both come from the same place
 * (claim vs row, both straight out of Data Connect) a plain compare is still correct and is left alone.
 */
export function sameDataConnectId(a, b) {
    if (!isDataConnectId(a) || !isDataConnectId(b))
        return false;
    const strip = (v) => v.replace(/-/g, "").toLowerCase();
    return strip(a) === strip(b);
}
export const DATACONNECT_ID_RE = /^(?:[0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
//# sourceMappingURL=contracts.js.map